import { io, Socket } from 'socket.io-client';
import type { BoardState, DrawnOpeningInfo, Move, TimeControlPreset, TimeControlKey, ClockSyncEvent, GameClockInit, ChatMessage, ArenaParticipantStatus, OriginePausaArena, ChallengeColor, ChallengeSettings, GameMode, LinkChallengeInfo, MatchLegs, OpeningMode, SpectateSnapshot, SpectateMove, SpectateTick, SpectateClosed, SpectateError, GameSpectatorsEvent, LobbyTableInfo, LobbyTableRemoved, LobbyCounts, LobbySnapshot, LobbyQuickResponse, LobbySitResponse } from '@draughts/shared';
import { GameVariant, TOLLERANZA_OROLOGIO_MS } from '@draughts/shared';
import { trackSocketDisconnected, trackSocketReconnected } from '../utils/analytics.js';
import { paginaNascosta } from '../hooks/usePaginaNascosta';
import { log } from '../utils/logger';
// Il rinnovo del token quando il server ci accetta come anonimi. `api` non
// importa questo modulo: nessun ciclo.
import { api } from './api';
import type {
  TournamentMatchData,
  TournamentStandingsUpdate,
  TournamentStanding,
  TournamentEndedData,
  TournamentGameEntry,
} from '../types/tournament';
import type {
  ArenaMatchData,
  ArenaReturnLegPending,
  ArenaStandingsUpdate,
  ArenaMyPositionUpdate,
  ArenaExcludedUpdate,
  ArenaStartedData,
  ArenaEndedData,
  ArenaGameCompletedPayload,
} from '../types/arena';
// Lobby types (public FIFO tables — the replacement for the ranked queue)
export interface LobbyError {
  code: string;
  message: string;
}

/**
 * A rejection that KEEPS its code. The overlay words each refusal differently
 * ("already in a game", "that table is gone", "leave the arena first"), and a
 * bare Error would have flattened them all into the server's English string.
 */
export class LobbyRequestError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'LobbyRequestError';
  }
}

// Tournament types
export interface TournamentJoinedData {
  tournamentId: string;
  position: number;
  playerCount: number;
}

export interface TournamentError {
  code: string;
  message: string;
}

const SERVER_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_SERVER_URL ?? '')
  : `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:3002`;

// Socket instance (singleton)
let socket: Socket | null = null;
let currentToken: string | null = null;
let connectCallbackCalled = false;
let hasBeenConnected = false;
let pendingIdCheckInterval: ReturnType<typeof setInterval> | null = null;
// Latched when the server evicts us with reason 'new-session' (another tab/device
// authenticated as the same user and took over). While set, connect() stays down so
// this older client does not auto-reconnect and fight for the session — that ping-pong
// drove ~2 same-user evictions/sec. Cleared by an explicit disconnectSocket()
// (e.g. identity change / logout), clearNewSessionEviction() or riprendiSessioneQui().
let newSessionEvicted = false;
export function wasEvictedByNewSession(): boolean {
  return newSessionEvicted;
}

/*
 * Chi vuole sapere quando il fermo sale o scende: l'avviso «aperto su un altro
 * dispositivo». Prima il fermo era muto — la scheda sembrava viva e non
 * riceveva piu' niente, senza una parola. Un insieme di ascoltatori come per
 * `subscribeSocketConnected`, per la stessa ragione: il registro dei callback
 * e' a chiavi, e un'altra chiave la ruberebbe a qualcuno.
 */
const ascoltatoriSessioneAltrove = new Set<() => void>();

/** Si iscrive ai cambi del fermo. La funzione restituita disiscrive. */
export function sottoscriviSessioneAltrove(ascoltatore: () => void): () => void {
  ascoltatoriSessioneAltrove.add(ascoltatore);
  return () => {
    ascoltatoriSessioneAltrove.delete(ascoltatore);
  };
}

/** L'unico modo di cambiare il fermo: chi lo cambia avvisa. */
function impostaSessioneAltrove(valore: boolean): void {
  if (newSessionEvicted === valore) return;
  newSessionEvicted = valore;
  for (const ascoltatore of ascoltatoriSessioneAltrove) {
    try {
      ascoltatore();
    } catch (err) {
      /* Un iscritto che esplode non deve zittire gli altri. */
      log.error('Socket', 'ascoltatore della sessione altrove fallito', err);
    }
  }
}

/*
 * Con quale identità è stata stabilita la connessione VIVA — non quella che
 * vorremmo avere. Il server legge il token solo dall'handshake
 * (socket.middleware.ts): assegnare `socket.auth` a connessione aperta non
 * cambia nulla lato server, e un socket nato anonimo resta anonimo finché non
 * si riconnette. Ogni evento autenticato che manda viene scartato in silenzio.
 */
let connectedUserId: string | null = null;

/*
 * Quale token e' stato DAVVERO consegnato all'handshake. `socket.auth` non e'
 * piu' un oggetto leggibile ma la funzione che lo consegna, quindi la sola
 * fonte su cosa il server ha ricevuto e' questa.
 */
let tokenConsegnatoAllHandshake: string | null = null;

/*
 * Un handshake e' cominciato e la sua callback di `auth` non ha ancora
 * consegnato. Serve a `setSocketAuthToken`: mentre questa e' alzata,
 * `socket.connect()` NON e' innocuo (vedi li' il perche').
 */
let handshakeInCorso = false;

/** userId dal payload del JWT. Serve solo a confrontare identità, non a fidarsi. */
function userIdFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { userId?: string }).userId ?? null;
  } catch {
    return null;
  }
}

/*
 * Tolleranza allo sfasamento fra l'orologio del client e quello del server. La
 * scadenza la giudicava solo il server; giudicandola qui, un client avanti di
 * qualche minuto scarterebbe un token appena emesso, `connectedUserId`
 * resterebbe `null` a ogni connect e ogni refresh riconnetterebbe: una
 * disconnessione ogni quarto d'ora, anche a metà partita.
 *
 * Copre lo sfasamento ORDINARIO. Uno sfasamento patologico (oltre i 15 minuti
 * di vita del token) resta scoperto, ma solo contro un server vecchio, cioè
 * nella finestra di rollout: col server nuovo `auth:accepted` sovrascrive
 * subito questa deduzione e il problema non si pone.
 *
 * ⚠️ Il numero è quello del SERVER, non un secondo numero. `verifyAccessToken`
 * verifica con la stessa tolleranza: erano due costanti in due pacchetti, e
 * divergere significava consegnare all'handshake un token che il server
 * rifiuta — connessione anonima, senza nemmeno un tentativo di rinnovo, perché
 * il client si credeva a posto. Adesso è una sola.
 */
const TOLLERANZA_SFASAMENTO_OROLOGIO_MS = TOLLERANZA_OROLOGIO_MS;

/**
 * userId dal JWT, ma SOLO se non è scaduto. `userIdFromToken` decodifica senza
 * verificare: un token scaduto restituisce lo userId lo stesso, e il chiamante
 * conclude di essere autenticato quando il server l'ha rifiutato.
 */
function userIdFromTokenNonScaduto(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { userId?: string; exp?: number };
    if (typeof claims.exp !== 'number') return null;
    if (claims.exp * 1000 + TOLLERANZA_SFASAMENTO_OROLOGIO_MS <= Date.now()) return null;
    return claims.userId ?? null;
  } catch {
    return null;
  }
}

export function clearNewSessionEviction(): void {
  impostaSessioneAltrove(false);
}

/**
 * «Usa qui»: riprende su QUESTO dispositivo la sessione che il server aveva
 * ceduto a un altro.
 *
 * E' la stessa riconnessione che il socket fa da solo dopo un calo di rete:
 * `connect` -> `handleConnected` -> rientro in partita, arena, torneo e
 * spettatore. Il server, vedendo arrivare questa connessione, chiude quella
 * dell'altro dispositivo con lo stesso `force-disconnect`, e lì compare lo
 * stesso avviso. Nessun ping-pong: da entrambe le parti si riparte solo a mano.
 *
 * Restituisce `false` se non c'era nulla da riprendere (nessun fermo, o nessun
 * socket: in quel caso basta aver abbassato il fermo, e la prossima `connect()`
 * degli store passa).
 */
export function riprendiSessioneQui(): boolean {
  if (!newSessionEvicted) return false;
  log.info('Socket', 'usa qui: riprendo la sessione su questo dispositivo');
  impostaSessioneAltrove(false);
  if (!socket) return false;
  /*
   * `opts.reconnection` il Manager lo legge solo alla costruzione: non e' lui
   * a tenere giu' il socket (lo fa la `disconnect()` del gestore di
   * `force-disconnect`). Ma `risvegliaSocket` lo usa come segnale di «fermo
   * voluto», e lasciarlo a `false` impedirebbe i risvegli di dopo.
   */
  socket.io.opts.reconnection = true;
  if (!socket.connected) socket.connect();
  return true;
}

/*
 * Come procurarsi un access token fresco. La registra l'auth-store: importarlo
 * qui chiuderebbe un ciclo (auth-store -> services/socket -> auth-store), come
 * si fa gia' per `api.setOnTokenRefreshed`.
 */
let richiediTokenFresco: (() => Promise<string | null>) | null = null;

export function setSocketTokenRefresher(fn: (() => Promise<string | null>) | null): void {
  richiediTokenFresco = fn;
}

/*
 * «Lo store crede che ci sia un'identita'», che non e' «c'e' un token».
 *
 * `accessToken` non e' persistito: al boot un utente registrato ha
 * `isAuthenticated: true` e `accessToken: null`, e il token va rifatto via rete
 * da `checkAuth()`. In quella finestra `currentToken` e' nullo, e il rinnovo si
 * autoescludeva -- «nessun token da rinnovare: qui non c'e' nessuna identita'
 * da salvare», diceva il commento, ma l'identita' c'era eccome: mancava solo
 * l'access token in memoria. Il socket nasceva anonimo e ci restava a tempo
 * indeterminato, perche' ne' `reconnect_attempt` ne' l'handshake rinnovavano
 * mai. L'unico recupero era un 401 su una chiamata REST -- che sulle pagine
 * arena e tornei, servite pubblicamente, non arriva mai.
 *
 * La registra l'auth-store, per la stessa ragione del refresher: importarlo qui
 * chiuderebbe un ciclo.
 */
let credeDiEssereAutenticato: (() => boolean) | null = null;

export function setSocketIdentitaAttesa(fn: (() => boolean) | null): void {
  credeDiEssereAutenticato = fn;
}

/** C'e' un'identita' da salvare, anche se il token in memoria non c'e'. */
function ciSiAspettaUnIdentita(): boolean {
  return currentToken !== null || credeDiEssereAutenticato?.() === true;
}

/** L'ultimo recupero tentato dietro un `auth:accepted` anonimo. */
let ultimoRecuperoAnonimo = 0;
/*
 * Quanti recuperi consecutivi si tentano prima di arrendersi.
 *
 * Il pavimento di dieci secondi limita la frequenza ma non ferma il ciclo: se
 * il server continua a non riconoscere il token rinnovato — segreto ruotato,
 * utente cancellato, sfasamento d'orologio nel verso opposto — si girerebbe
 * per sempre a circa sei `POST /api/auth/refresh` al minuto PER CLIENT. Su un
 * droplet da 1 vCPU e' l'inondazione che il resto di questo file passa il tempo
 * a evitare. Il contatore si azzera al primo `auth:accepted` che ci riconosce.
 */
const MAX_RECUPERI_ANONIMI = 3;
let recuperiAnonimiConsecutivi = 0;

/**
 * Il server ci ha accettati da anonimi, ma un'identita' ce l'avevamo: si rinnova
 * il token e si riconnette.
 *
 * Riconnettere e' l'unico modo: il server legge il token SOLO dall'handshake
 * (`socket.middleware.ts`), quindi un token fresco su una connessione gia'
 * aperta non cambia niente.
 *
 * Un tentativo solo per volta, con lo stesso intervallo minimo degli altri
 * percorsi: un rinnovo che fallisce e riconnette e fallisce di nuovo sarebbe un
 * ciclo, e su un droplet da 1 vCPU un ciclo per client e' un'inondazione.
 */
async function recuperaIdentitaAnonima(): Promise<void> {
  if (recuperiAnonimiConsecutivi >= MAX_RECUPERI_ANONIMI) {
    log.warn('Socket', 'socket anonimo: gia\' tentato abbastanza, mi arrendo');
    return;
  }
  if (haDettoRallenta()) {
    log.warn('Socket', 'socket anonimo: il server ha chiesto di rallentare, non insisto');
    return;
  }
  const adesso = Date.now();
  if (adesso - ultimoRecuperoAnonimo < INTERVALLO_MINIMO_RINNOVO_MS) {
    log.warn('Socket', 'socket anonimo: recupero gia\' tentato da poco, non insisto');
    return;
  }
  ultimoRecuperoAnonimo = adesso;
  recuperiAnonimiConsecutivi += 1;

  log.warn('Socket', 'socket anonimo con identita\' attesa: rinnovo e riconnetto', {
    tentativo: recuperiAnonimiConsecutivi,
  });
  if (!(await rinnovaIdentitaConTetto())) {
    log.warn('Socket', 'socket anonimo: rinnovo fallito, si resta anonimi');
    return;
  }
  /*
   * La riconnessione col token fresco NON parte da qui.
   *
   * `unRinnovo` ha gia' scritto `currentToken`, quindi un
   * `setSocketAuthToken(currentToken)` sarebbe per costruzione un no-op — il
   * suo `tokenChanged` sarebbe sempre falso. A riconnettere e' la via lunga,
   * che parte dallo stesso rinnovo: `api.tryRefresh` -> `onTokenRefreshed` ->
   * auth-store -> `setSocketAuthToken(nuovo)`, dove l'identita' cambia davvero.
   * E' una dipendenza reale e vale la pena nominarla, invece di lasciarla
   * sembrare un effetto di questa riga.
   */
}


/**
 * Set the auth token for socket connections
 * Forces reconnect if socket exists to apply new auth
 */
export function setSocketAuthToken(token: string | null): void {
  log.info('Socket', 'setSocketAuthToken called', { hasToken: !!token });
  const tokenChanged = currentToken !== token;
  currentToken = token;

  // If socket exists and token changed, update auth
  if (socket && tokenChanged && token) {
    /*
     * Niente da scrivere su `socket.auth`: e' la funzione `tokenPerHandshake`,
     * che al prossimo handshake legge `currentToken` da sola. Sovrascriverla
     * con un oggetto spegnerebbe il rinnovo per tutta la vita del socket.
     */
    if (!socket.connected) {
      /*
       * Se un handshake e' gia' in corso, il token nuovo e' proprio quello che
       * la sua callback sta aspettando: parte con lui. Chiamare `connect()`
       * qui manderebbe un SECONDO pacchetto CONNECT — col trasporto gia'
       * aperto socket.io richiama `onopen()`, che ri-invoca la callback di
       * `auth` (socket.js:193-201, 423-428) — e sarebbero due socket lato
       * server, `connection.handler` due volte, doppia registrazione fra gli
       * utenti in linea e l'eviction same-user che puo' scattare contro la
       * sessione appena nata. E' il percorso NORMALE del rinnovo riuscito:
       * `onTokenRefreshed` rientra qui mentre l'ack del CONNECT non e' ancora
       * arrivato, quindi `socket.connected` e' ancora falso.
       */
      if (handshakeInCorso) {
        log.info('Socket', 'handshake in corso: il token nuovo parte con quello, non riconnetto');
      } else {
        socket.connect();
      }
    } else if (connectedUserId !== userIdFromToken(token)) {
      /*
       * La connessione viva appartiene a un'altra identità (tipicamente:
       * nessuna, perché il socket è nato prima che il token esistesse).
       * Assegnare `socket.auth` non basta — il server lo legge solo
       * all'handshake — quindi va rifatta. Se invece è lo stesso utente e il
       * token è solo stato ruotato dal refresh, non si tocca niente: sarebbe
       * una disconnessione ogni quarto d'ora, anche in mezzo a una partita.
       */
      log.info('Socket', 'auth: connessione viva di un\'altra identità, riconnetto', {
        connectedUserId, nuovo: userIdFromToken(token),
      });
      socket.disconnect();
      socket.connect();
    }
  }
}

/** `isGuest` dal payload del JWT: i token dei registrati non lo portano, quindi vale `false`. */
function isGuestFromToken(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { isGuest?: boolean }).isGuest === true;
  } catch {
    return false;
  }
}

/*
 * Il token e' cambiato ma l'identita' no: e' il rinnovo che ruota il token di
 * accesso ogni 16 minuti. Rifare l'handshake non darebbe al server niente di
 * nuovo — il token lo legge solo li', e a connessione aperta la scadenza non la
 * guarda piu' — e costerebbe una caduta vera: nel torneo del 1 ottobre ~25 in
 * 72 minuti, una delle quali mentre si apriva la stanza del turno.
 *
 * Le tre condizioni, e perche' tutte:
 * - connesso: un socket giu' va connesso comunque, col token nuovo;
 * - lo userId e' quello CONFERMATO dal server (`auth:accepted`), non quello
 *   letto dal token dell'handshake: se il server l'avesse rifiutato, il socket
 *   sarebbe anonimo pur avendo «consegnato» un token dall'aria valida;
 * - stesso `isGuest`: l'ospite che diventa registrato tiene lo userId, ma il
 *   server legge `isGuest` solo all'handshake e lo terrebbe fuori dai tornei.
 *
 * Fra 'connect' e 'auth:accepted' (un viaggio di rete) `connectedUserId` e'
 * ancora quello dedotto dal token consegnato. Se proprio li' il server l'avesse
 * rifiutato, la riconnessione salta qui ma non si perde: `auth:accepted` con
 * `userId: null` porta a `recuperaIdentitaAnonima`, che rinnova e riconnette.
 */
function eSoloUnaRotazione(token: string | null): boolean {
  return !!socket?.connected
    && connectedUserId !== null
    && connectedUserId === userIdFromToken(token)
    && isGuestFromToken(token) === isGuestFromToken(tokenConsegnatoAllHandshake);
}

/**
 * Ensure socket is connected with proper authentication
 * Forces reconnection if socket exists but auth doesn't match current token
 * Returns a promise that resolves when socket is connected and authenticated
 */
export async function ensureSocketAuth(token: string | null): Promise<void> {
  log.info('Socket', 'ensureSocketAuth called', { hasToken: !!token, hasSocket: !!socket });
  currentToken = token;

  if (!socket) {
    // No socket exists, will be created by initSocket
    log.info('Socket', 'ensureSocketAuth: no socket, skipping');
    return;
  }

  /*
   * Il token della connessione VIVA e' quello consegnato all'handshake, non
   * quello che vorremmo avere: `socket.auth` e' la funzione che lo consegna e
   * non si puo' interrogare. Non c'e' niente da assegnare — la riconnessione
   * qui sotto rifara' l'handshake, e allora `currentToken` sara' quello nuovo.
   */
  if (tokenConsegnatoAllHandshake !== token && eSoloUnaRotazione(token)) {
    log.info('Socket', 'ensureSocketAuth: token solo ruotato, il socket resta');
    return;
  }

  if (tokenConsegnatoAllHandshake !== token) {
    log.info('Socket', 'ensureSocketAuth: token mismatch, reconnecting');

    if (socket.connected) {
      // Disconnect and wait for reconnection
      return new Promise((resolve) => {
        const onConnect = () => {
          socket?.off('connect', onConnect);
          log.info('Socket', 'ensureSocketAuth: reconnected after auth change');
          resolve();
        };
        socket?.on('connect', onConnect);
        socket?.disconnect();
        socket?.connect();

        // Timeout after 3 seconds
        setTimeout(() => {
          socket?.off('connect', onConnect);
          resolve();
        }, 3000);
      });
    } else {
      socket.connect();
    }
  } else {
    log.info('Socket', 'ensureSocketAuth: auth already matches');
  }
}

// Rating update from server after game ends
export interface RatingUpdate {
  oldRating: number;
  newRating: number;
  oldRd: number;
  newRd: number;
}

export interface GameRatings {
  white: RatingUpdate;
  black: RatingUpdate;
}

// Event callbacks
/**
 * Drawn-opening info surfaced on game:start / rematch:start (challenge flow).
 *
 * `DrawnOpeningInfo` porta anche `id`, il numero di tabella federale — presente solo
 * quando la pescata viene davvero da una riga di tabella (vedi `ballotNumber`).
 */
export type DrawnOpeningPayload = { mode: 'free' | 'drawn' } & DrawnOpeningInfo;
/**
 * Quanto resta per giocare la PRIMA mossa vera, e cosa succede se non arriva.
 *
 * Una DURATA e non un istante: così il conto non dipende dall'orologio del
 * dispositivo, che può essere sfasato di minuti. Il campo manca del tutto
 * quando non c'è scadenza — in torneo, dove l'orologio parte subito, o a prima
 * mossa già giocata.
 *
 * L'arbitro resta il server (`ClockManager`): questo serve solo al conto a
 * schermo.
 */
export type PrimaMossaPayload = { restaMs: number; esito: 'void' | 'forfeit' };

/**
 * Tutto quello che il server manda all'avvio di una partita.
 *
 * Un oggetto e non undici argomenti posizionali: la lista era arrivata al
 * limite in cui aggiungere un campo significa contare le virgole in tre file.
 */
export interface GameStartPayload {
  state: BoardState;
  clock: GameClockInit | null;
  color?: 'white' | 'black';
  roomId?: string;
  opponent?: { username: string; rating?: number | null; avatar?: string | null; country?: string | null };
  variant?: string;
  timeControl?: string;
  opening?: DrawnOpeningPayload;
  rated?: boolean;
  match?: MatchInfoPayload;
  source?: string;
  primaMossa?: PrimaMossaPayload;
  /**
   * La posizione da cui la partita e' COMINCIATA, presente solo quando non e'
   * l'apertura standard: una sfida nata dall'editor (diretta, da link o da un
   * tavolo di lobby con posizione).
   *
   * E' quello che `game-store.startBoard` ricostruisce: la revisione rigioca
   * `moveHistory` da li'. Senza questo campo partiva dall'apertura standard,
   * `applyMove` lanciava sulla prima mossa e frecce, cursore e lista mosse non
   * facevano niente — mentre «vai all'inizio» mostrava una posizione in cui la
   * partita non e' mai stata. Assente = apertura standard, cioe' il caso di
   * ogni partita normale (e di ogni server che non lo manda ancora).
   */
  startBoard?: BoardState;
}

type GameStartCallback = (dati: GameStartPayload) => void;
/**
 * `roomId` e' l'ultimo argomento, ed e' opzionale, per una ragione sola: nella
 * finestra di deploy un server vecchio non lo manda. Chi lo riceve instrada per
 * identita'; chi non lo riceve ripiega sull'euristica di prima.
 */
type GameUpdateCallback = (state: BoardState, lastMove: Move, clock: ClockSyncEvent | null, roomId?: string) => void;
/**
 * Two-leg match ("andata e ritorno") progress. Present only on drawn-opening
 * direct challenges, where the same opening is played twice with swapped colors.
 * `score` is keyed by userId — colors swap between legs, so a color-keyed score
 * would be meaningless.
 */
export interface MatchInfoPayload {
  legIndex: number;
  totalLegs: number;
  complete: boolean;
  score: Record<string, number>;
}
type GameEndCallback = (
  result: 'white' | 'black' | 'draw',
  reason: string,
  ratings?: GameRatings,
  sessionScore?: { white: number; black: number },
  canRematch?: boolean,
  match?: MatchInfoPayload,
  /** La stanza che finisce. Assente con un server piu' vecchio del deploy. */
  roomId?: string
) => void;
type GameErrorCallback = (message: string, code?: string) => void;
type GameAbortedCallback = (data: {
  role: 'stay' | 'left';
  /**
   * Perche' la partita e' stata annullata. Non e' piu' sempre
   * 'opponent_abandoned': `no_first_move` dice che l'avversario non ha mosso in
   * tempo, che e' un'altra cosa dall'essersene andato — e il giocatore merita
   * di sapere quale delle due.
   */
  reason: string;
  /**
   * Rimettersi in coda da soli ha senso solo se in coda ci si era davvero: un
   * tavolo rapido. Una sfida diretta o una rivincita annullata riporta al menu.
   * Deciso dal server (`room.matchmaking`), non indovinato qui.
   */
  requeue?: boolean;
  timeControl?: string;
  variant?: string;
}) => void;
type ConnectionCallback = () => void;
type ClockSyncCallback = (clock: ClockSyncEvent) => void;
type LobbyTableAddedCallback = (table: LobbyTableInfo) => void;
type LobbyTableRemovedCallback = (data: LobbyTableRemoved) => void;
type LobbyCountsCallback = (counts: LobbyCounts) => void;

// Arena types
export interface ArenaJoinedData {
  arenaId: string;
  position: number;
  playerCount: number;
}

export interface ArenaError {
  code: string;
  message: string;
}

// Arena callbacks
type ArenaJoinedCallback = (data: ArenaJoinedData) => void;
type ArenaLeftCallback = (data: { arenaId: string | null }) => void;
type ArenaMatchCallback = (data: ArenaMatchData) => void;
type ArenaStandingsCallback = (data: ArenaStandingsUpdate) => void;
type ArenaMyPositionCallback = (data: ArenaMyPositionUpdate) => void;
type ArenaExcludedCallback = (data: ArenaExcludedUpdate) => void;
type ArenaStartedCallback = (data: ArenaStartedData) => void;
type ArenaEndedCallback = (data: ArenaEndedData) => void;
/*
 * La conferma porta lo stato del partecipante, non solo l'id dell'arena: e' la
 * sola cosa che permette allo store di riallineare **entrambe** le sue verita'
 * (`status` e `myParticipant.status`) invece di una sola. Vedi
 * `setArenaPaused` in arena-store.
 */
type ArenaPausedCallback = (data: { arenaId: string; participantStatus?: ArenaParticipantStatus; origine?: OriginePausaArena }) => void;
type ArenaResumedCallback = (data: { arenaId: string; participantStatus?: ArenaParticipantStatus }) => void;
type ArenaErrorCallback = (error: ArenaError) => void;
type ArenaGameCompletedCallback = (data: ArenaGameCompletedPayload) => void;
type ArenaListUpdateCallback = (data: { arenaId: string; playerCount: number }) => void;
type ArenaCreatedCallback = (data: { arenaId: string }) => void;
/**
 * La lista delle arene non e' piu' quella: una e' nata, una e' partita o una e'
 * finita. Nessun payload di proposito — chi ascolta se la rilegge da se'.
 * Da non confondere con `ArenaListUpdateCallback` (singolare, senza la "d"),
 * che porta il conteggio dei giocatori di UNA arena.
 */
type ArenaListUpdatedCallback = () => void;
type ArenaRewatchCallback = (data: { arenaId: string }) => void;

// Tournament callbacks
type TournamentJoinedCallback = (data: TournamentJoinedData) => void;
// `string | null`: il server risponde `null` quando non ha trovato niente da
// cancellare, ed e' su quel caso che si regge la guardia contro la bugia
// «non sei piu' iscritto».
type TournamentLeftCallback = (data: { tournamentId: string | null }) => void;
type TournamentMatchCallback = (data: TournamentMatchData) => void;
type TournamentStandingsCallback = (data: TournamentStandingsUpdate) => void;
/*
 * L'avvio di un torneo. `format` e `twoLeg` sono quelli DECISI all'avvio: un
 * girone-svizzero parte come 'swiss' e ne puo' uscire girone, con il ritorno
 * acceso da tre o quattro iscritti. Opzionali per un server vecchio durante un
 * deploy.
 */
export interface TournamentStartedData {
  tournamentId: string;
  totalRounds?: number;
  format?: 'swiss' | 'round_robin' | 'arena';
  twoLeg?: boolean;
}
type TournamentStartedCallback = (data: TournamentStartedData) => void;
type TournamentEndedCallback = (data: TournamentEndedData) => void;
type TournamentErrorCallback = (error: TournamentError) => void;
type TournamentGameCompletedCallback = (data: { tournamentId?: string; game: TournamentGameEntry }) => void;
/** Il turno di riposo: il server lo manda al solo interessato. */
type TournamentByeCallback = (data: { tournamentId: string; roundNumber: number; pointsAwarded: number }) => void;
type TournamentListUpdateCallback = (data: { tournamentId: string; playerCount: number }) => void;
type TournamentCreatedCallback = (data: { tournamentId: string }) => void;

// Chat callbacks
type GameChatCallback = (message: ChatMessage) => void;

// Opponent callbacks (for private rooms)
type OpponentJoinedCallback = (data: { username?: string }) => void;

// Online callbacks
export interface OnlineUser {
  userId: string;
  username: string;
  avatar?: string | null;
  /** `null` per ospiti e bot. Vedi la gemella in store/online-store.ts. */
  rating: number | null;
  status: 'available' | 'playing' | 'away';
  /** Ospite (i bot inclusi). Vedi la gemella in store/online-store.ts. */
  isGuest?: boolean;
  /** Con chi sta giocando. Solo sulle righe `playing`. Vedi la gemella. */
  opponent?: string;
}

// Discriminated union matching the server emits: 'joined' carries the full user,
// 'left' carries { userId, username }, and 'status' is flat (no user/count wrapper).
export type OnlineUpdateData =
  | { type: 'joined'; user: { userId: string; username: string; avatar?: string | null; rating?: number | null; status?: string; isGuest?: boolean }; count: number }
  | { type: 'left'; user: { userId: string; username: string }; count: number }
  /* `opponent` viaggia solo con `status: 'playing'`, e puo' mancare: la stanza
     puo' non esserci ancora. Chi lo riceve lo usa per il badge; chi non lo
     riceve lo recupera al prossimo `online:list`. */
  | { type: 'status'; userId: string; status: string; opponent?: string }
  /** Il loro punteggio e' cambiato (fine partita valutata): solo gli id, il numero se lo chiede chi guarda. */
  | { type: 'rating'; userIds: string[] };

type OnlineUpdateCallback = (data: OnlineUpdateData) => void;

// Challenge callbacks
export interface Challenge {
  id: string;
  fromUserId: string;
  fromUsername: string;
  /** `null` per ospiti e bot: nessun punteggio da mostrare. */
  fromRating: number | null;
  toUserId: string;
  toUsername: string;
  variant: string;
  timeControl: string;
  color: ChallengeColor;
  rated: boolean;
  gameMode: GameMode;
  opening: OpeningMode;
  /** 2 = andata e ritorno (drawn openings only), 1 = solo andata. */
  legs: MatchLegs;
  status: string;
  createdAt: number;
  expiresAt: number;
}

type ChallengeReceivedCallback = (challenge: Challenge) => void;
type ChallengeAcceptedCallback = (challenge: Challenge) => void;
type ChallengeDeclinedCallback = (challenge: Challenge) => void;
type ChallengeCancelledCallback = (challenge: Challenge) => void;

// Challenge-link callbacks (shareable "invita un amico" links)
type LinkChallengeCallback = (link: LinkChallengeInfo) => void;

// Draw offer callbacks
type DrawOfferedCallback = (data: { roomId: string }) => void;
type DrawDeclinedCallback = (data: { roomId: string }) => void;

// Takeback callbacks (casual online only)
type TakebackOfferedCallback = (data: { roomId: string; toPly: number; plies: number }) => void;
type TakebackDeclinedCallback = (data: { roomId: string }) => void;
type TakebackAcceptedCallback = (data: {
  roomId: string;
  toPly: number;
  state: BoardState;
  clock: ClockSyncEvent | null;
}) => void;

// Rematch callbacks
/** `expiresAt` (epoch ms) is the server's deadline for the offer — the receiver
 *  counts down from it, and gets it echoed in the `rematch:request` ack too. */
type RematchRequestedCallback = (data: { roomId: string; expiresAt?: number }) => void;
type RematchDeclinedCallback = (data: { roomId: string }) => void;
/** The offer timed out. NOT a refusal: the room survives and it can be re-offered. */
type RematchExpiredCallback = (data: { roomId: string }) => void;
type RematchStartCallback = (data: {
  roomId: string;
  state: BoardState;
  clock: GameClockInit | null;
  color: 'white' | 'black';
  forcedOpening?: { id: number; moves: string[]; forza: string; displayText: string };
  /** Challenge-flow drawn opening re-drawn on rematch (for the in-game banner). */
  opening?: DrawnOpeningPayload;
  /** Present when the rematch starts a new two-leg match (drawn opening). */
  match?: MatchInfoPayload;
  sessionScore: { white: number; black: number };
  // Full opponent profile so a player who left the game (and cleared their
  // local opponent) gets a complete card on the rematch, not "Anonymous".
  opponent?: { username: string; rating?: number | null; avatar?: string | null; country?: string | null };
  /** Stanza riusata = partita nuova: la scadenza della prima mossa riparte. */
  primaMossa?: PrimaMossaPayload;
  /*
   * Di che partita si tratta. Chi era uscito dalla schermata di fine partita ha
   * azzerato il suo stato: senza questi due l'intestazione si rimette sul
   * default — «Internazionale», cadenza «—» — su una damiera che e' di un'altra
   * variante, e il punteggio della card arriva dal bucket sbagliato.
   * Facoltativi per i server che non li mandano ancora.
   */
  variant?: GameVariant;
  timeControl?: TimeControlKey;
  /** La posizione di partenza composta: vedi `GameStartPayload.startBoard`. */
  startBoard?: BoardState;
}) => void;
// Global rematch invite (opponent left the game-over screen; surfaced like a challenge)
export interface RematchInviteData {
  roomId: string;
  fromUserId?: string;
  fromUsername: string;
  /** `null` per ospiti e bot: nessun punteggio da mostrare. */
  fromRating: number | null;
  variant: GameVariant;
  timeControl: TimeControlPreset | 'none';
  expiresAt: number;
}
type RematchInviteCallback = (data: RematchInviteData) => void;
type RematchInviteExpiredCallback = (data: { roomId: string }) => void;

// Two-leg match callbacks (drawn-opening challenges)
/** Leg 1 is over; the return leg auto-starts at `startsAt`. */
export interface MatchReturnPendingData {
  roomId: string;
  legIndex: number;
  nextLegIndex: number;
  totalLegs: number;
  startsAt: number;
  serverTime: number;
  score: Record<string, number>;
  opening?: { displayText?: string; moves?: string[] };
}
type MatchReturnPendingCallback = (data: MatchReturnPendingData) => void;
type MatchReadyStateCallback = (data: { roomId: string; readyUserIds: string[] }) => void;
/** The return leg starting: same shape as rematch:start plus the match block. */
export interface MatchLegStartData {
  roomId: string;
  state: BoardState;
  clock: GameClockInit | null;
  color: 'white' | 'black';
  sessionScore: { white: number; black: number };
  opening?: DrawnOpeningPayload;
  opponent?: { username: string; rating?: number | null; avatar?: string | null; country?: string | null };
  match?: MatchInfoPayload;
  primaMossa?: PrimaMossaPayload;
  /** Come in `rematch:start`: la stanza e' la stessa, lo stato del client no. */
  variant?: GameVariant;
  timeControl?: TimeControlKey;
  /*
   * La posizione di partenza composta: vedi `GameStartPayload.startBoard`.
   *
   * Qui sara' sempre assente — una sfida a due manche non puo' partire da una
   * posizione composta — ma il campo esce dalla stessa funzione del server che
   * serve `rematch:start`, e un ramo che lascia cadere un campo che arriva e'
   * esattamente il difetto da cui nasce `rivincita-variante.cablaggio`.
   */
  startBoard?: BoardState;
}
type MatchLegStartCallback = (data: MatchLegStartData) => void;

// Disconnection/Reconnection callbacks
/* `roomId` e' facoltativo solo per i server che non lo mandano ancora: il client
   lo usa per scartare gli avvisi di una partita che non e' piu' la sua. */
type PlayerDisconnectedCallback = (data: {
  roomId?: string;
  color: 'white' | 'black';
  graceTimeMs: number;
  /** Nessun forfait a fine grazia: se non torna, perde per tempo (torneo, prima della sua prima mossa). */
  decideLOrologio?: boolean;
  /** Non si e' mai presentato: la stanza e' stata aperta senza di lui. */
  maiArrivato?: boolean;
}) => void;
type PlayerReconnectedCallback = (data: { roomId?: string; color: 'white' | 'black' }) => void;
type GameRejoinedCallback = (data: {
  roomId: string;
  state: BoardState;
  clock: ClockSyncEvent | null;
  color: 'white' | 'black';
  /**
   * Di chi e' la partita. Stesso patto di `check-active`: senza questi due il
   * multiplayer si prende anche le partite di torneo e di arena, e chi ricarica
   * la pagina mentre gioca resta sulla schermata sbagliata per tutto il torneo.
   */
  tournamentId?: string | null;
  arenaId?: string | null;
  /** Nella forma di `check-active`, cosi' il nome non passa da «Anonimo». */
  opponent?: { oddsUserId?: string; oddsUserName?: string; oddsRating?: number; oddsAvatar?: string | null } | null;
  variant?: string;
  timeControl?: string;
  /** La conversazione com'era: il transcript vive sul server, lo store riparte vuoto. */
  chat?: ChatMessage[];
  /** Quanto resta alla prima mossa: il rientro nella grazia e' il ricarica-pagina. */
  primaMossa?: PrimaMossaPayload;
  /** Le semi-mosse messe dal sorteggio (il server le manda da sempre). */
  openingPlyCount?: number;
  /**
   * L'apertura sorteggiata, nella forma di `game:start`; assente senza apertura.
   * Senza, chi rientra perdeva l'intestazione «APERTURA N° …» e i grigi in Mosse.
   */
  opening?: DrawnOpeningPayload;
  /**
   * La manche di una partita di competizione a due partite (torneo, arena): la
   * porta solo chi ne ha davvero due. L'accoppiamento la annuncia una volta
   * sola e il ritorno non la rimanda, quindi senza questo campo un ricarico
   * perdeva il «2/2» sopra la damiera.
   */
  manche?: { leg: number; totalLegs: number };
  /** La posizione di partenza composta: vedi `GameStartPayload.startBoard`. */
  startBoard?: BoardState;
}) => void;

interface SocketCallbacks {
  onGameStart?: GameStartCallback;
  /**
   * Il resoconto del rientro, da applicare allo stato.
   *
   * Esiste perche' `risvegliaSocket` — l'unico percorso che gira quando l'app
   * torna in primo piano SENZA che il socket sia caduto — riceveva la risposta
   * a `game:check-active` e la buttava via, guardando solo se fosse scaduta.
   * Su una partita finita mentre si era via quella risposta e' l'unico
   * messaggero dell'esito (`game:end` va alla room, e chi si e' disconnesso
   * l'ha gia' lasciata), quindi buttarla via lasciava a schermo una damiera
   * viva su cui non si poteva ne' muovere ne' abbandonare.
   *
   * La registra il multiplayer-store su `applyReconnectResult`, lo stesso
   * codice che serve `onConnect`.
   */
  onRientro?: (risposta: CheckActiveGameResponse) => void;
  onGameUpdate?: GameUpdateCallback;
  onGameEnd?: GameEndCallback;
  onGameAborted?: GameAbortedCallback;
  onGameError?: GameErrorCallback;
  onConnect?: ConnectionCallback;
  onDisconnect?: ConnectionCallback;
  onClockSync?: ClockSyncCallback;
  onLobbyTableAdded?: LobbyTableAddedCallback;
  onLobbyTableRemoved?: LobbyTableRemovedCallback;
  onLobbyCounts?: LobbyCountsCallback;
  // Arena
  onArenaJoined?: ArenaJoinedCallback;
  onArenaLeft?: ArenaLeftCallback;
  onArenaMatch?: ArenaMatchCallback;
  onArenaReturnLegPending?: (data: ArenaReturnLegPending) => void;
  onArenaStandings?: ArenaStandingsCallback;
  onArenaMyPosition?: ArenaMyPositionCallback;
  onArenaExcluded?: ArenaExcludedCallback;
  onArenaStarted?: ArenaStartedCallback;
  onArenaEnded?: ArenaEndedCallback;
  /**
   * L'arena che all'ora d'inizio non aveva abbastanza iscritti (migration 104).
   *
   * Non e' `arena:ended`: quella ha una classifica e un podio, questa non e'
   * mai cominciata. Senza un evento suo, chi era rimasto sulla pagina vedeva
   * «non e' ancora cominciata» per sempre — l'arena non compare piu' fra le
   * attive e nessun altro annuncio la riguarda.
   */
  /*
   * `iscritti` e `minimo` sono FACOLTATIVI: hanno senso solo per il motivo
   * `no_players` — «quanti erano, quanti ne servivano». Quando a chiudere
   * l'arena e' chi l'ha aperta (`motivo: 'organizer'`) quei due numeri non
   * esistono, e pretenderli avrebbe costretto il server a inventarne due.
   */
  onArenaCancelled?: (data: { arenaId: string; motivo: string; iscritti?: number; minimo?: number }) => void;
  onArenaPaused?: ArenaPausedCallback;
  onArenaResumed?: ArenaResumedCallback;
  onArenaError?: ArenaErrorCallback;
  onArenaGameCompleted?: ArenaGameCompletedCallback;
  onArenaListUpdate?: ArenaListUpdateCallback;
  onArenaCreated?: ArenaCreatedCallback;
  onArenaListUpdated?: ArenaListUpdatedCallback;
  /**
   * Il socket è tornato e la stanza dell'arena è stata ripresa. Non è un evento
   * del server: lo emette `rearmArena` da questo modulo. Chiave sua, così
   * nessuno store ha bisogno di registrare `onConnect` per accorgersene.
   */
  onArenaRewatch?: ArenaRewatchCallback;
  // Tournament
  onTournamentJoined?: TournamentJoinedCallback;
  onTournamentLeft?: TournamentLeftCallback;
  onTournamentMatch?: TournamentMatchCallback;
  onTournamentStandings?: TournamentStandingsCallback;
  onTournamentStarted?: TournamentStartedCallback;
  onTournamentEnded?: TournamentEndedCallback;
  onTournamentError?: TournamentErrorCallback;
  onTournamentGameCompleted?: TournamentGameCompletedCallback;
  onTournamentBye?: TournamentByeCallback;
  /**
   * Quante partite del turno sono concluse: il server lo manda a tutta la
   * stanza del torneo a ogni partita che finisce.
   */
  onSwissRoundProgress?: (data: { tournamentId: string; roundNumber: number; completedGames: number; totalGames: number }) => void;
  /**
   * Il ritorno di uno svizzero a due manche sta per cominciare (migr. 105).
   *
   * Ai due soli giocatori, non alla stanza del torneo. Senza, chi ha appena
   * finito l'andata leggerebbe «in attesa del prossimo turno» — che e' falso:
   * il turno e' lo stesso, e fra pochi secondi si rigioca contro lo stesso
   * avversario a colori invertiti.
   */
  onSwissReturnLegPending?: (data: {
    tournamentId: string;
    roundNumber: number;
    startsAt: number;
    serverTime: number;
    opening?: { displayText: string; moves: string[] };
  }) => void;
  onTournamentListUpdate?: TournamentListUpdateCallback;
  /**
   * Il torneo e' stato annullato: da chi l'ha aperto, o dal ciclo di vita
   * perche' non ci e' arrivato nessuno. Il server lo manda nella stanza del
   * torneo (`broadcastTournamentCancelled`), e finora non lo ascoltava nessuno.
   */
  onTournamentCancelled?: (data: { tournamentId: string; reason: 'organizer' | 'no_players' }) => void;
  onTournamentCreated?: TournamentCreatedCallback;
  onTournamentRoundStart?: (data: { roundNumber: number; totalRounds: number; tournamentId?: string }) => void;
  /* La classifica di fine turno viaggia DENTRO l'evento: e' quella con gli
     spareggi appena ricalcolati, l'unica che il server manda fino al turno dopo. */
  onTournamentRoundEnd?: (data: { roundNumber: number; tournamentId?: string; standings?: TournamentStanding[] }) => void;
  // Chat
  onGameChat?: GameChatCallback;
  /**
   * Chi sta guardando questa partita. Arriva SOLO ai due giocatori: il server lo
   * manda alla stanza di gioco, dove uno spettatore non entra mai.
   */
  onGameSpectators?: (data: GameSpectatorsEvent) => void;
  /** A partita finita l'avversario è uscito (true) o è rientrato (false). */
  onOpponentPresence?: (data: { roomId: string; left: boolean }) => void;
  /**
   * La finestra della chat del dopo-partita: `closesAt` è il preavviso (chiude a
   * quell'istante), `null` è la chiusura avvenuta.
   *
   * Lo dice il server perché il suo timer si ri-arma — fra le due manche di un
   * match, o con un invito di rivincita ancora aperto — quindi un orologio locale
   * mentirebbe. Vedi `chat-window.ts` lato server.
   */
  onChatWindow?: (data: { roomId: string; closesAt: number | null }) => void;
  // Private rooms
  onOpponentJoined?: OpponentJoinedCallback;
  // Online users
  onOnlineUpdate?: OnlineUpdateCallback;
  // Challenges
  onChallengeReceived?: ChallengeReceivedCallback;
  onChallengeAccepted?: ChallengeAcceptedCallback;
  onChallengeDeclined?: ChallengeDeclinedCallback;
  onChallengeCancelled?: ChallengeCancelledCallback;
  // Challenge links
  /** A friend opened the shared link and is now waiting for the game to start. */
  onLinkChallengeClaimed?: LinkChallengeCallback;
  /** The friend left the waiting room — the link is up for grabs again. */
  onLinkChallengeReleased?: LinkChallengeCallback;
  /** The challenger cancelled the link (or replaced it with a new one). */
  onLinkChallengeCancelled?: LinkChallengeCallback;
  /** The link passed its hour without a game starting. */
  onLinkChallengeExpired?: LinkChallengeCallback;
  // Draw offer
  onDrawOffered?: DrawOfferedCallback;
  onDrawDeclined?: DrawDeclinedCallback;
  // Takeback
  onTakebackOffered?: TakebackOfferedCallback;
  onTakebackDeclined?: TakebackDeclinedCallback;
  onTakebackAccepted?: TakebackAcceptedCallback;
  // Rematch
  onRematchRequested?: RematchRequestedCallback;
  onRematchDeclined?: RematchDeclinedCallback;
  onRematchExpired?: RematchExpiredCallback;
  onRematchStart?: RematchStartCallback;
  onRematchInvite?: RematchInviteCallback;
  onRematchInviteExpired?: RematchInviteExpiredCallback;
  onMatchReturnPending?: MatchReturnPendingCallback;
  onMatchReadyState?: MatchReadyStateCallback;
  onMatchLegStart?: MatchLegStartCallback;
  // Disconnection/Reconnection
  onPlayerDisconnected?: PlayerDisconnectedCallback;
  onPlayerReconnected?: PlayerReconnectedCallback;
  onGameRejoined?: GameRejoinedCallback;
  // Spectating someone else's live game. Deliberately its own event family:
  // the game:* callbacks above are routed into the player-side stores, so a
  // spectator reusing them would corrupt the viewer's own game state.
  onSpectateMove?: (data: SpectateMove) => void;
  onSpectateTick?: (data: SpectateTick) => void;
  onSpectateClosed?: (data: SpectateClosed) => void;
  /** A fresh snapshot pushed by the re-arm after a reconnect (see `spectatingUserId`). */
  onSpectateSnapshot?: (data: SpectateSnapshot) => void;
  /** The re-arm failed — the watched game is over or gone. */
  onSpectateUnavailable?: (data: SpectateError) => void;
}

let callbacks: SocketCallbacks = {};

/**
 * Initialize socket connection
 */
export function initSocket(cbs: SocketCallbacks): Socket {
  log.info('Socket', 'initSocket called', { callbackKeys: Object.keys(cbs), hasExistingSocket: !!socket, isConnected: socket?.connected ?? false });

  // Clear stale callbacks before merging to prevent old handlers from
  // accumulating across multiple initSocket() calls
  const freshCallbacks: SocketCallbacks = {};
  for (const key of Object.keys(cbs) as (keyof SocketCallbacks)[]) {
    freshCallbacks[key] = cbs[key] as never;
  }
  // Preserve callbacks from other stores that aren't being overwritten
  callbacks = { ...callbacks, ...freshCallbacks };

  if (socket?.connected) {
    log.info('Socket', 'initSocket: reusing existing connected socket', { id: socket.id });
    return socket;
  }

  // Clear any pending ID check interval from a previous initSocket call
  if (pendingIdCheckInterval) {
    clearInterval(pendingIdCheckInterval);
    pendingIdCheckInterval = null;
  }

  // Clean up any existing disconnected/reconnecting socket to prevent
  // orphaned event handlers from firing (e.g., stale onDisconnect events
  // that would reset status to 'disconnected' after a new socket is created)
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  connectCallbackCalled = false; // Reset for new socket initialization

  socket = io(SERVER_URL, {
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 15,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 3000,
    /*
     * `auth` nella forma a FUNZIONE: socket.io la valuta nell'istante esatto in
     * cui sta per mandare il pacchetto CONNECT, e il pacchetto aspetta che la
     * callback scatti (socket.io-client 4.8.3, socket.js:425-428). E' cosi' che
     * un rinnovo asincrono fa in tempo con certezza, invece di correre contro
     * l'handshake.
     */
    auth: tokenPerHandshake,
  });

  log.info('Socket', 'Socket created', { serverUrl: SERVER_URL, hasAuth: !!currentToken });

  // Manager-level reconnection lifecycle (log-only). Without these, after the
  // 15th failed attempt the app is dead but the UI still looks alive.
  socket.io?.on('reconnect_attempt', async (attempt) => {
    log.warn('Socket', 'reconnect_attempt', { attempt });
    /*
     * Socket.IO riconnette con il token che ha in mano. Dopo un'app rimasta in
     * secondo piano quel token e' scaduto: il server accetta l'handshake in
     * ANONIMO, il giocatore non e' piu' riconosciuto e la partita si perde a
     * tavolino (caso c2ba015d, 5'34" ancora sull'orologio). Prima, il rinnovo
     * partiva 32 secondi dopo, innescato dai 401 delle chiamate HTTP.
     * Questo e' l'ultimo momento utile per rinnovarlo.
     */
    await rinnovaTokenSeScaduto();
  });
  socket.io?.on('reconnect', (attempt) => {
    log.info('Socket', 'reconnect succeeded', { attempt });
  });
  socket.io?.on('reconnect_error', (error) => {
    log.warn('Socket', 'reconnect_error', { message: (error as Error)?.message });
  });
  socket.io?.on('reconnect_failed', () => {
    log.error('Socket', 'reconnect_failed — giving up (app is dead)');
  });

  /*
   * ATTENZIONE: 'auth:accepted' parte una volta sola, sincronicamente all'atto
   * della connessione (connection.handler.ts). L'ascoltatore va registrato qui,
   * prima che la connessione possa completarsi: più tardi lo si perde in
   * silenzio, senza alcun errore.
   */
  socket.on('auth:accepted', ({ userId, expired }: { userId: string | null; expired: boolean }) => {
    /*
     * Questa è l'UNICA fonte di verità su chi siamo per il server. Prima veniva
     * dedotta decodificando il nostro stesso token — e un JWT scaduto si
     * decodifica benissimo, quindi un socket anonimo sembrava autenticato e non
     * riconnetteva mai più (partita c2ba015d, persa a tempo con 5'34" residui).
     */
    log.info('Socket', 'auth:accepted: identità accettata dal server', { userId, expired });
    connectedUserId = userId;
    /* Riconosciuti: il contatore dei recuperi riparte da zero. */
    if (userId !== null && !expired) recuperiAnonimiConsecutivi = 0;

    /*
     * E se il server dice che non ci ha riconosciuti, si fa qualcosa.
     *
     * `expired` era destrutturato via dal tipo e mai letto, e `userId: null` si
     * limitava a scrivere `connectedUserId = null`: nessun rinnovo, nessuna
     * riconnessione, nessun avviso. Fuori dal percorso partita — dove
     * `checkActiveGame` recupera per conto suo — un socket anonimo non guariva
     * da solo: in arena, in lobby, in coda l'utente sembrava connesso, spariva
     * dalla lista degli utenti in linea (il server registra solo gli
     * autenticati) e ogni evento autenticato veniva rifiutato in silenzio.
     *
     * Si agisce solo se un'identita' ce la aspettiamo: un visitatore anonimo e'
     * anonimo per davvero, e rinnovare per lui sarebbe una richiesta HTTP a
     * ogni connessione senza niente da salvare.
     */
    if (userId !== null && !expired) {
      /* Riconosciuti: se la presenza in arena e' stata spedita un istante
         troppo presto — su un socket ancora anonimo — `handleArenaBack` l'ha
         scartata in silenzio, perche' esige `isAuthenticated`. Adesso vale. */
      ridichiaraPresenzaArena();
      return;
    }
    if (!credeDiEssereAutenticato?.()) return;
    void recuperaIdentitaAnonima();
  });

  // Connection events
  socket.on('connect', () => {
    /*
     * Identità PROVVISORIA, valida solo contro un server che non manda ancora
     * 'auth:accepted' (deploy non atomico): appena l'evento arriva, la
     * sovrascrive. Un token scaduto vale `null`, ed è esattamente il caso che
     * prima faceva sembrare autenticato un socket anonimo.
     */
    connectedUserId = userIdFromTokenNonScaduto(tokenConsegnatoAllHandshake);

    // Prevent calling onConnect multiple times per connection attempt
    if (connectCallbackCalled) {
      return;
    }

    // Only trigger onConnect when socket has a valid ID
    // Socket.IO can fire 'connect' before handshake is complete
    const handleConnected = (): void => {
      log.info('Socket', 'Connected', { id: socket?.id, isReconnect: hasBeenConnected });
      if (hasBeenConnected) {
        trackSocketReconnected();
      }
      hasBeenConnected = true;
      connectCallbackCalled = true;
      callbacks.onConnect?.();
      rearmSpectating();
      rearmArena();
      riagganciaTorneo();
      ridichiaraPresenzaArena();
      avvisaConnessione();
    };

    if (socket?.id) {
      handleConnected();
    } else {
      // Clear any previous pending check before starting a new one
      if (pendingIdCheckInterval) {
        clearInterval(pendingIdCheckInterval);
      }
      // Fallback: wait for ID to become available
      pendingIdCheckInterval = setInterval(() => {
        if (socket?.id && !connectCallbackCalled) {
          clearInterval(pendingIdCheckInterval!);
          pendingIdCheckInterval = null;
          handleConnected();
        }
      }, 50);
      // Give up after 2 seconds
      const intervalRef = pendingIdCheckInterval;
      setTimeout(() => {
        clearInterval(intervalRef);
        if (pendingIdCheckInterval === intervalRef) {
          pendingIdCheckInterval = null;
        }
      }, 2000);
    }
  });

  socket.on('disconnect', (reason) => {
    log.warn('Socket', 'Disconnected', { reason });
    connectCallbackCalled = false; // Reset so next connection triggers callback
    arenaPresenzaRidichiarataSu = null;
    if (hasBeenConnected) {
      trackSocketDisconnected(reason);
    }
    callbacks.onDisconnect?.();
  });

  socket.on('connect_error', (error) => {
    log.error('Socket', 'Connection error', { message: error.message, hasBeenConnected });
  });

  socket.on('force-disconnect', (data: { reason: string }) => {
    log.warn('Socket', 'Force-disconnected by server', { reason: data?.reason ?? 'unknown reason', reconnectionWas: socket!.io.opts.reconnection });
    socket!.io.opts.reconnection = false;
    // Don't let this (superseded) client reconnect and re-evict the newer session.
    if (data?.reason === 'new-session') impostaSessioneAltrove(true);
    socket!.disconnect();
  });

  // Game events
  socket.on('game:start', (data: Omit<GameStartPayload, 'clock'> & { clock?: GameClockInit }) => {
    log.info('Socket', 'game:start received', { variant: data.state.variant, hasClock: !!data.clock, color: data.color, roomId: data.roomId, opponent: data.opponent?.username, opponentRating: data.opponent?.rating, timeControl: data.timeControl, opening: data.opening?.mode, rated: data.rated, matchLegs: data.match?.totalLegs, primaMossa: data.primaMossa });
    callbacks.onGameStart?.({ ...data, clock: data.clock ?? null });
  });

  socket.on('game:update', (data: { roomId?: string; state: BoardState; lastMove: Move; clock?: ClockSyncEvent }) => {
    log.info('Socket', 'game:update received', { roomId: data.roomId, from: data.lastMove.from, to: data.lastMove.to, currentTurn: data.state.currentTurn });
    callbacks.onGameUpdate?.(data.state, data.lastMove, data.clock ?? null, data.roomId);
  });

  socket.on('game:end', (data: {
    roomId?: string;
    result: 'white' | 'black' | 'draw';
    reason: string;
    ratings?: GameRatings;
    sessionScore?: { white: number; black: number };
    canRematch?: boolean;
    match?: MatchInfoPayload;
  }) => {
    log.info('Socket', 'game:end received', { roomId: data.roomId, result: data.result, reason: data.reason, matchLeg: data.match?.legIndex });
    callbacks.onGameEnd?.(data.result, data.reason, data.ratings, data.sessionScore, data.canRematch, data.match, data.roomId);
  });

  socket.on('game:aborted', (data: { role: 'stay' | 'left'; reason: string; requeue?: boolean; timeControl?: string; variant?: string }) => {
    log.info('Socket', 'game:aborted received', { role: data.role, reason: data.reason });
    callbacks.onGameAborted?.(data);
  });

  socket.on('game:error', (data: { message: string; code?: string }) => {
    log.error('Socket', 'game:error received', data.message);
    callbacks.onGameError?.(data.message, data.code);
  });

  // Clock sync event (server-side clock)
  socket.on('game:clock-sync', (data: ClockSyncEvent) => {
    callbacks.onClockSync?.(data);
  });

  // Lobby events. There is no 'match found': a table becomes a game, and the
  // game announces itself through the ordinary `game:start`.
  socket.on('lobby:table-added', (table: LobbyTableInfo) => {
    callbacks.onLobbyTableAdded?.(table);
  });

  socket.on('lobby:table-removed', (data: LobbyTableRemoved) => {
    callbacks.onLobbyTableRemoved?.(data);
  });

  socket.on('online:counts', (counts: LobbyCounts) => {
    callbacks.onLobbyCounts?.(counts);
  });

  // Arena events
  socket.on('arena:match', (data: ArenaMatchData) => {
    callbacks.onArenaMatch?.(data);
  });

  socket.on('arena:return-leg-pending', (data: ArenaReturnLegPending) => {
    callbacks.onArenaReturnLegPending?.(data);
  });

  socket.on('arena:standings', (data: ArenaStandingsUpdate) => {
    callbacks.onArenaStandings?.(data);
  });

  socket.on('arena:my-position', (data: ArenaMyPositionUpdate) => {
    callbacks.onArenaMyPosition?.(data);
  });

  socket.on('arena:excluded', (data: ArenaExcludedUpdate) => {
    callbacks.onArenaExcluded?.(data);
  });

  socket.on('arena:started', (data: ArenaStartedData) => {
    callbacks.onArenaStarted?.(data);
  });

  socket.on('arena:ended', (data: ArenaEndedData) => {
    callbacks.onArenaEnded?.(data);
  });

  socket.on('arena:cancelled', (data: { arenaId: string; motivo: string; iscritti?: number; minimo?: number }) => {
    callbacks.onArenaCancelled?.(data);
  });

  socket.on('arena:paused', (data: { arenaId: string; participantStatus?: ArenaParticipantStatus; origine?: OriginePausaArena }) => {
    callbacks.onArenaPaused?.(data);
  });

  socket.on('arena:resumed', (data: { arenaId: string; participantStatus?: ArenaParticipantStatus }) => {
    callbacks.onArenaResumed?.(data);
  });

  socket.on('arena:left', (data: { arenaId: string | null }) => {
    callbacks.onArenaLeft?.(data);
  });

  socket.on('arena:error', (data: ArenaError) => {
    log.error('Socket', 'arena:error', data.message);
    callbacks.onArenaError?.(data);
  });

  socket.on('arena:game-completed', (data: ArenaGameCompletedPayload) => {
    callbacks.onArenaGameCompleted?.(data);
  });

  socket.on('arena:list-update', (data: { arenaId: string; playerCount: number }) => {
    callbacks.onArenaListUpdate?.(data);
  });

  socket.on('arena:created', (data: { arenaId: string }) => {
    callbacks.onArenaCreated?.(data);
  });

  socket.on('arena:list-updated', () => {
    callbacks.onArenaListUpdated?.();
  });

  // Tournament events
  socket.on('tournament:joined', (data: TournamentJoinedData) => {
    callbacks.onTournamentJoined?.(data);
  });

  socket.on('tournament:left', (data: { tournamentId: string }) => {
    callbacks.onTournamentLeft?.(data);
  });

  socket.on('tournament:match', (data: TournamentMatchData) => {
    callbacks.onTournamentMatch?.(data);
  });

  socket.on('tournament:standings', (data: TournamentStandingsUpdate) => {
    callbacks.onTournamentStandings?.(data);
  });

  socket.on('tournament:started', (data: TournamentStartedData) => {
    callbacks.onTournamentStarted?.(data);
  });

  socket.on('tournament:ended', (data: TournamentEndedData) => {
    callbacks.onTournamentEnded?.(data);
  });

  socket.on('tournament:error', (data: TournamentError) => {
    log.error('Socket', 'tournament:error', data.message);
    callbacks.onTournamentError?.(data);
  });

  socket.on('tournament:game-completed', (data: { tournamentId?: string; game: TournamentGameEntry }) => {
    callbacks.onTournamentGameCompleted?.(data);
  });

  socket.on('tournament:list-update', (data: { tournamentId: string; playerCount: number }) => {
    callbacks.onTournamentListUpdate?.(data);
  });

  socket.on('tournament:cancelled', (data: { tournamentId: string; reason: 'organizer' | 'no_players' }) => {
    callbacks.onTournamentCancelled?.(data);
  });

  socket.on('tournament:created', (data: { tournamentId: string }) => {
    callbacks.onTournamentCreated?.(data);
  });

  socket.on('tournament:round-start', (data: { tournamentId: string; roundNumber: number; totalRounds: number }) => {
    callbacks.onTournamentRoundStart?.(data);
  });

  socket.on('tournament:round-end', (data: { tournamentId: string; roundNumber: number; standings?: TournamentStanding[] }) => {
    callbacks.onTournamentRoundEnd?.(data);
  });

  /*
   * Il turno di riposo. Il server lo manda da sempre (FIX-38) al solo
   * interessato, e il client non lo ascoltava: chi riceveva il bye restava sui
   * puntini di «in attesa di avversario» per tutto il turno, senza sapere che
   * quel turno riposava e che il punto era gia' suo.
   */
  socket.on('swiss:return-leg-pending', (data: {
    tournamentId: string;
    roundNumber: number;
    startsAt: number;
    serverTime: number;
    opening?: { displayText: string; moves: string[] };
  }) => {
    callbacks.onSwissReturnLegPending?.(data);
  });

  socket.on('tournament:bye', (data: { tournamentId: string; roundNumber: number; pointsAwarded: number }) => {
    callbacks.onTournamentBye?.(data);
  });

  socket.on('swiss:round-progress', (data: { tournamentId: string; roundNumber: number; completedGames: number; totalGames: number }) => {
    callbacks.onSwissRoundProgress?.(data);
  });

  // Chat events
  socket.on('game:chat', (data: ChatMessage) => {
    log.info('Socket', 'game:chat received', { senderColor: data.senderColor });
    callbacks.onGameChat?.(data);
  });

  // Senza log: viaggia sul battito a 1 Hz del riconciliatore, come spectate:tick.
  socket.on('game:spectators', (data: GameSpectatorsEvent) => {
    callbacks.onGameSpectators?.(data);
  });

  socket.on('game:opponent-left', (data: { roomId: string }) => {
    log.info('Socket', 'game:opponent-left received', { roomId: data.roomId });
    callbacks.onOpponentPresence?.({ roomId: data.roomId, left: true });
  });

  socket.on('game:opponent-returned', (data: { roomId: string }) => {
    log.info('Socket', 'game:opponent-returned received', { roomId: data.roomId });
    callbacks.onOpponentPresence?.({ roomId: data.roomId, left: false });
  });

  socket.on('game:chat-closing', (data: { roomId: string; closesAt: number }) => {
    log.info('Socket', 'game:chat-closing received', { roomId: data.roomId, closesAt: data.closesAt });
    callbacks.onChatWindow?.({ roomId: data.roomId, closesAt: data.closesAt });
  });

  socket.on('game:chat-closed', (data: { roomId: string }) => {
    log.info('Socket', 'game:chat-closed received', { roomId: data.roomId });
    callbacks.onChatWindow?.({ roomId: data.roomId, closesAt: null });
  });

  // Private room events
  socket.on('opponent:joined', (data: { username?: string }) => {
    log.info('Socket', 'opponent:joined', { username: data.username });
    callbacks.onOpponentJoined?.(data);
  });

  // Online user events
  socket.on('online:update', (data: OnlineUpdateData) => {
    log.info('Socket', 'online:update', { type: data.type });
    callbacks.onOnlineUpdate?.(data);
  });

  // Challenge events
  socket.on('challenge:received', (data: Challenge) => {
    log.info('Socket', 'challenge:received', { from: data.fromUsername });
    callbacks.onChallengeReceived?.(data);
  });

  socket.on('challenge:accepted', (data: Challenge) => {
    log.info('Socket', 'challenge:accepted');
    callbacks.onChallengeAccepted?.(data);
  });

  socket.on('challenge:declined', (data: Challenge) => {
    log.info('Socket', 'challenge:declined');
    callbacks.onChallengeDeclined?.(data);
  });

  socket.on('challenge:cancelled', (data: Challenge) => {
    log.info('Socket', 'challenge:cancelled');
    callbacks.onChallengeCancelled?.(data);
  });

  // Challenge-link events. The game itself always arrives as game:start, so
  // these only drive the two waiting screens (challenger overlay / friend's
  // waiting room).
  socket.on('challenge:link-claimed', (data: LinkChallengeInfo) => {
    log.info('Socket', 'challenge:link-claimed', { by: data.claimedByUsername });
    callbacks.onLinkChallengeClaimed?.(data);
  });

  socket.on('challenge:link-released', (data: LinkChallengeInfo) => {
    log.info('Socket', 'challenge:link-released');
    callbacks.onLinkChallengeReleased?.(data);
  });

  socket.on('challenge:link-cancelled', (data: LinkChallengeInfo) => {
    log.info('Socket', 'challenge:link-cancelled');
    callbacks.onLinkChallengeCancelled?.(data);
  });

  socket.on('challenge:link-expired', (data: LinkChallengeInfo) => {
    log.info('Socket', 'challenge:link-expired');
    callbacks.onLinkChallengeExpired?.(data);
  });

  // Draw offer events
  socket.on('game:draw-offered', (data: { roomId: string }) => {
    log.info('Socket', 'game:draw-offered', { roomId: data.roomId });
    callbacks.onDrawOffered?.(data);
  });

  socket.on('game:draw-declined', (data: { roomId: string }) => {
    log.info('Socket', 'game:draw-declined', { roomId: data.roomId });
    callbacks.onDrawDeclined?.(data);
  });

  // Takeback events (casual online only)
  socket.on('game:takeback-offered', (data: { roomId: string; toPly: number; plies: number }) => {
    log.info('Socket', 'game:takeback-offered', { roomId: data.roomId, toPly: data.toPly, plies: data.plies });
    callbacks.onTakebackOffered?.(data);
  });

  socket.on('game:takeback-declined', (data: { roomId: string }) => {
    log.info('Socket', 'game:takeback-declined', { roomId: data.roomId });
    callbacks.onTakebackDeclined?.(data);
  });

  socket.on('game:takeback-accepted', (data: { roomId: string; toPly: number; state: BoardState; clock: ClockSyncEvent | null }) => {
    log.info('Socket', 'game:takeback-accepted', { roomId: data.roomId, toPly: data.toPly });
    callbacks.onTakebackAccepted?.(data);
  });

  // Rematch events
  socket.on('rematch:requested', (data: { roomId: string; expiresAt?: number }) => {
    log.info('Socket', 'rematch:requested', { roomId: data.roomId, expiresAt: data.expiresAt });
    callbacks.onRematchRequested?.(data);
  });

  socket.on('rematch:declined', (data: { roomId: string }) => {
    log.info('Socket', 'rematch:declined', { roomId: data.roomId });
    callbacks.onRematchDeclined?.(data);
  });

  socket.on('rematch:expired', (data: { roomId: string }) => {
    log.info('Socket', 'rematch:expired', { roomId: data.roomId });
    callbacks.onRematchExpired?.(data);
  });

  socket.on('rematch:start', (data: { roomId: string; state: BoardState; clock?: GameClockInit; color: 'white' | 'black'; sessionScore: { white: number; black: number }; forcedOpening?: { id: number; moves: string[]; forza: string; displayText: string }; opening?: DrawnOpeningPayload; match?: MatchInfoPayload; opponent?: { username: string; rating?: number | null; avatar?: string | null; country?: string | null }; primaMossa?: PrimaMossaPayload; variant?: GameVariant; timeControl?: TimeControlKey; startBoard?: BoardState }) => {
    log.info('Socket', 'rematch:start', { color: data.color, roomId: data.roomId, variant: data.variant, timeControl: data.timeControl });
    callbacks.onRematchStart?.({
      roomId: data.roomId,
      state: data.state,
      clock: data.clock ?? null,
      color: data.color,
      sessionScore: data.sessionScore,
      forcedOpening: data.forcedOpening,
      opening: data.opening,
      match: data.match,
      opponent: data.opponent,
      // Stanza riusata = partita nuova = scadenza della prima mossa riarmata.
      primaMossa: data.primaMossa,
      /*
       * E questi due sono il motivo per cui il tipo inline qui sopra va tenuto
       * allineato a mano con quello del callback: sono due dichiarazioni
       * separate, e un campo che arriva ma non viene inoltrato muore qui in
       * silenzio. E' esattamente cosi' che la variante si era persa.
       */
      variant: data.variant,
      timeControl: data.timeControl,
      /* E la posizione di partenza: la rivincita di una partita composta
         riparte dalla stessa, e la revisione la ricostruisce solo da qui. */
      startBoard: data.startBoard,
    });
  });

  // Global rematch invite: opponent requested a rematch while this client had
  // already left the game. Surfaced via GlobalIncomingRematchBanner.
  socket.on('rematch:invite', (data: RematchInviteData) => {
    log.info('Socket', 'rematch:invite', { from: data.fromUsername, roomId: data.roomId });
    callbacks.onRematchInvite?.(data);
  });

  socket.on('rematch:invite-expired', (data: { roomId: string }) => {
    log.info('Socket', 'rematch:invite-expired', { roomId: data.roomId });
    callbacks.onRematchInviteExpired?.(data);
  });

  // Two-leg match events (drawn-opening challenges): the return leg is not a
  // rematch — it starts on its own after a short countdown.
  socket.on('match:return-pending', (data: MatchReturnPendingData) => {
    log.info('Socket', 'match:return-pending', { roomId: data.roomId, startsAt: data.startsAt });
    callbacks.onMatchReturnPending?.(data);
  });

  socket.on('match:ready-state', (data: { roomId: string; readyUserIds: string[] }) => {
    callbacks.onMatchReadyState?.(data);
  });

  socket.on('match:leg-start', (data: {
    roomId: string;
    state: BoardState;
    clock?: GameClockInit;
    color: 'white' | 'black';
    sessionScore: { white: number; black: number };
    opening?: DrawnOpeningPayload;
    opponent?: { username: string; rating?: number | null; avatar?: string | null; country?: string | null };
    match?: MatchInfoPayload;
    primaMossa?: PrimaMossaPayload;
    variant?: GameVariant;
    timeControl?: TimeControlKey;
    startBoard?: BoardState;
  }) => {
    log.info('Socket', 'match:leg-start', { roomId: data.roomId, color: data.color, leg: data.match?.legIndex, opening: data.opening?.mode, variant: data.variant });
    callbacks.onMatchLegStart?.({
      roomId: data.roomId,
      state: data.state,
      clock: data.clock ?? null,
      color: data.color,
      sessionScore: data.sessionScore,
      opening: data.opening,
      opponent: data.opponent,
      match: data.match,
      primaMossa: data.primaMossa,
      variant: data.variant,
      timeControl: data.timeControl,
      /* Oggi sempre assente (una sfida a due manche non e' mai composta), e
         inoltrata comunque: arriva dalla stessa funzione del server che serve
         `rematch:start`, e un campo che non si inoltra muore qui in silenzio. */
      startBoard: data.startBoard,
    });
  });

  // Disconnection/Reconnection events
  socket.on('game:player-disconnected', (data: Parameters<PlayerDisconnectedCallback>[0]) => {
    log.info('Socket', 'game:player-disconnected', { roomId: data.roomId, color: data.color, graceTimeMs: data.graceTimeMs, decideLOrologio: data.decideLOrologio, maiArrivato: data.maiArrivato });
    callbacks.onPlayerDisconnected?.(data);
  });

  socket.on('game:player-reconnected', (data: { roomId?: string; color: 'white' | 'black' }) => {
    log.info('Socket', 'game:player-reconnected', { roomId: data.roomId, color: data.color });
    callbacks.onPlayerReconnected?.(data);
  });

  socket.on('game:rejoined', (data: { roomId: string; state: BoardState; clock: ClockSyncEvent | null; color: 'white' | 'black'; tournamentId?: string | null; arenaId?: string | null; opponent?: { oddsUserId?: string; oddsUserName?: string; oddsRating?: number; oddsAvatar?: string | null } | null; variant?: string; timeControl?: string; chat?: ChatMessage[]; primaMossa?: PrimaMossaPayload }) => {
    log.info('Socket', 'game:rejoined', { roomId: data.roomId, color: data.color, variant: data.variant, tournamentId: data.tournamentId, arenaId: data.arenaId });
    callbacks.onGameRejoined?.(data);
  });

  // ── Spectating ────────────────────────────────────────────────────────────
  socket.on('spectate:move', (data: SpectateMove) => {
    callbacks.onSpectateMove?.(data);
  });
  // No log here: this is the 1 Hz heartbeat, the spectator twin of game:clock-sync.
  socket.on('spectate:tick', (data: SpectateTick) => {
    callbacks.onSpectateTick?.(data);
  });
  socket.on('spectate:closed', (data: SpectateClosed) => {
    log.info('Socket', 'spectate:closed', { roomId: data.roomId, reason: data.reason });
    // Nothing left to re-arm to: forget the target so a later reconnect can't
    // resurrect the subscription against that player's next game.
    if (data.roomId === spectatingRoomId) {
      spectatingUserId = null;
      spectatingRoomId = null;
    }
    callbacks.onSpectateClosed?.(data);
  });

  return socket;
}

/**
 * Get current socket instance
 */
export function getSocket(): Socket | null {
  return socket;
}

/**
 * Check if socket is connected
 */
export function isConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Check if user has an active game and rejoin if so
 */
/** Terminal fields returned when reconnecting to a game that already ENDED. */
export interface EndedGameInfo {
  result: 'white' | 'black' | 'draw';
  reason: string;
  sessionScore?: { white: number; black: number };
  canRematch?: boolean;
  match?: MatchInfoPayload;
}

export interface CheckActiveGameResponse {
  hasActiveGame: boolean;
  /**
   * Il server non ha potuto guardare: la connessione e' anonima (tipicamente un
   * token scaduto accettato come tale). NON e' "non hai partite in corso" — chi
   * rientra cosi' e' proprio chi una partita ce l'ha, e mentre si rassegna il
   * grace period scorre (partita c2ba015d, persa a tempo con 5'34" residui).
   */
  unauthenticated?: boolean;
  roomId?: string;
  state?: BoardState;
  clock?: ClockSyncEvent | null;
  color?: 'white' | 'black';
  opponent?: { oddsUserId?: string; oddsUserName?: string; oddsRating?: number; oddsAvatar?: string | null } | null;
  variant?: string;
  timeControl?: string;
  arenaId?: string;
  tournamentId?: string;
  /** La conversazione della room, da rimettere nello store dopo il contesto. */
  chat?: ChatMessage[];
  /**
   * Quante semi-mosse in `state.moveHistory` le ha messe il sorteggio
   * d'apertura, e se il boost d'arena e' acceso. Servono al bottone del boost,
   * che senza questi due dovrebbe indovinare: contava le semi-mosse
   * dell'apertura come mosse giocate e ripartiva da "spento" su un boost che
   * il server aveva acceso.
   */
  openingPlyCount?: number;
  /** L'apertura sorteggiata, nella forma di `game:start`: vedi `GameRejoinedCallback`. */
  opening?: DrawnOpeningPayload;
  /**
   * La manche di una partita di competizione a due partite (torneo, arena): la
   * porta solo chi ne ha davvero due. L'accoppiamento la annuncia una volta
   * sola e il ritorno non la rimanda, quindi senza questo campo un ricarico
   * perdeva il «2/2» sopra la damiera.
   */
  manche?: { leg: number; totalLegs: number };
  /**
   * La posizione di partenza composta: vedi `GameStartPayload.startBoard`.
   * Il server la manda su entrambi i rami — partita viva e partita finita
   * mentre si era via — perche' la scheda Mosse si naviga anche dopo l'esito.
   */
  startBoard?: BoardState;
  boosted?: boolean;
  /**
   * Quanto resta alla prima mossa. Per la stessa ragione dei due campi qui
   * sopra: senza, chi rientra riparte con un conto pieno mentre sul server il
   * tempo e' gia' corso, e il numero a schermo mente.
   */
  primaMossa?: PrimaMossaPayload;
  /**
   * Le negoziazioni pendenti al momento del rientro: chi ha offerto patta (il
   * suo COLORE, non il socketId) e l'eventuale richiesta di takeback.
   *
   * Il payload del rientro non le portava, e nessun evento le riallineava: chi
   * aveva offerto patta non vedeva piu' «proposta inviata» e chi doveva
   * risponderle non vedeva piu' la richiesta, mentre il server l'offerta la
   * teneva. Lo stesso ragionamento che era stato fatto per `boosted` e non per
   * loro.
   */
  drawOfferBy?: 'WHITE' | 'BLACK' | null;
  takebackOffer?: { color: 'WHITE' | 'BLACK'; toPly: number } | null;
  /**
   * Il freno delle proposte di patta per il posto di chi rientra.
   *
   * Senza, un ricarico rimetteva il bottone acceso su una quota esaurita e il
   * primo gesto era un rifiuto — ed e' il ricarico il varco che il vecchio
   * limite per connessione lasciava aperto, perche' era chiavato sul socket.
   */
  offertePatta?: { restanti: number; deviMuovere: boolean };
  // Present (with hasActiveGame:false) when the player reconnected to a game that
  // ended while they were away — e.g. a disconnection forfeit that never delivered
  // game:end. The client renders game-over + rematch from this instead of a frozen board.
  endedGame?: EndedGameInfo | null;
  /**
   * Il server non ha risposto entro il tetto d'attesa: NON e' "non hai
   * partite". Tenuto distinto — come `unauthenticated` — perche' le due
   * risposte sarebbero altrimenti lo stesso oggetto, e chi rientra
   * rilascerebbe una partita viva convinto che il server abbia guardato.
   */
  timedOut?: boolean;
  /**
   * Present when reconnecting BETWEEN the two legs of a match: leg 1's result is
   * in `endedGame`, and the return leg auto-starts at `startsAt`.
   */
  returnLeg?: {
    startsAt: number | null;
    legIndex: number;
    totalLegs: number;
    score: Record<string, number>;
  };
}

/*
 * Tetto d'attesa per l'ack di `game:check-active`. Un ack che non arriva non lo
 * segnala nessuno: senza tetto la promessa resta appesa per sempre, il
 * giocatore non rientra al suo tavolo, non vede nessun errore, e il grace
 * period del server gli scorre addosso fino alla sconfitta a tavolino.
 *
 * Il valore NON e' scelto a naso: e' vincolato dall'invariante di
 * `ATTESA_MASSIMA_RIENTRO_MS` qui sotto, che un test sorveglia — se qualcuno
 * alza questo numero (o uno qualunque degli altri tetti del rientro) il test
 * cade, quindi questo commento non puo' invecchiare in silenzio. Col budget al
 * 65%% del grace il massimo ammesso e' 3_500; preso il valore tondo sotto,
 * 3_000, per cui il conto fa 18_000 su 19_500 concessi.
 *
 * Dentro questo tetto non c'e' solo la rete del giocatore: c'e' anche il tempo
 * del SERVER. Il gestore di `game:check-active` fa i suoi giri al database per
 * torneo e arena prima di rispondere, e gira su un droplet da 1 vCPU. Scadere
 * troppo presto sarebbe il male peggiore — si rilascerebbe una partita viva,
 * magari una che il server ha appena ripristinato — ma una scadenza singola NON
 * e' una resa: costa una ripetizione. Perche' si rilasci la partita servono due
 * silenzi di fila, cioe' sei secondi buoni senza risposta.
 */
export const ATTESA_ACK_CHECK_ACTIVE_MS = 3_000;

function emettiCheckActive(): Promise<CheckActiveGameResponse> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      log.warn('Socket', 'checkActiveGame: socket non connesso, non c\'e\' niente da chiedere');
      resolve({ hasActiveGame: false });
      return;
    }

    let concluso = false;
    const concludi = (risposta: CheckActiveGameResponse): void => {
      if (concluso) return;
      concluso = true;
      clearTimeout(timer);
      resolve(risposta);
    };
    const timer = setTimeout(() => {
      log.warn('Socket', 'check-active: nessun ack entro il tetto', { entroMs: ATTESA_ACK_CHECK_ACTIVE_MS });
      concludi({ hasActiveGame: false, timedOut: true });
    }, ATTESA_ACK_CHECK_ACTIVE_MS);

    log.info('Socket', 'emit game:check-active');
    socket.emit('game:check-active', (response: CheckActiveGameResponse) => {
      concludi(response);
    });
  });
}

/** Quanto si aspetta l'annuncio d'identita' dopo un rinnovo del token. */
export const ATTESA_IDENTITA_MS = 3_000;

/*
 * Intervallo minimo fra due rinnovi chiesti da `checkActiveGame`. Ogni
 * riconnessione richiama `checkActiveGame`, e un rinnovo che riesce ma continua
 * a produrre un socket anonimo riconnetterebbe di nuovo: senza questo intervallo
 * un client in quello stato martella /api/auth/refresh, e il droplet ha 1 vCPU.
 */
const INTERVALLO_MINIMO_RINNOVO_MS = 10_000;
let ultimoRinnovoPerCheckActive = 0;

/*
 * Attese fra i tentativi di rinnovo di UNA richiesta. Il fallimento piu'
 * frequente e' transitorio — e' proprio il blip di rete di chi sta rientrando —
 * e arrendersi al primo colpo gli fa rilasciare una partita ancora viva.
 *
 * Sono un tentativo solo, scaglionato, non richieste indipendenti: l'intervallo
 * minimo qui sopra si consulta una volta per richiesta e NON li sopprime.
 *
 * Un'attesa sola, non due: ogni gradino e' una `POST /api/auth/refresh` in
 * piu', e il limitatore del server ne concede 30 in 15 minuti per IP. Con due
 * gradini un rientro andato male ne produceva quindici, e due rientri
 * sfortunati esaurivano la finestra — a quel punto arrivano i 429, che prima
 * venivano pure ritentati. Un gradino solo dimezza il traffico del rientro e
 * lascia comunque un ritentativo al blip di rete, che e' il caso frequente.
 */
export const ATTESE_FRA_I_RINNOVI_MS = [600];

const attendi = (ms: number): Promise<void> => new Promise((esci) => setTimeout(esci, ms));

/**
 * Un solo tentativo di rinnovo. `true` se ce l'ha fatta.
 *
 * Col refresher registrato dall'auth-store il token fresco torna indietro e
 * finisce SUBITO in `currentToken`, che e' quello che `tokenPerHandshake`
 * consegna. Senza refresher si passa comunque da `api`, che aggiorna
 * `currentToken` per la via lunga
 * (`onTokenRefreshed` -> auth-store -> `setSocketAuthToken`).
 */
async function unRinnovo(): Promise<boolean> {
  if (!richiediTokenFresco) return api.refreshTokenRequest();
  const fresco = await richiediTokenFresco();
  if (!fresco) return false;
  currentToken = fresco;
  return true;
}

/**
 * Il server ha appena chiesto di rallentare (429 dal rate limiter). Non e' un
 * guasto da riprovare: ritentare aggiunge richieste a una finestra gia'
 * esaurita e non puo' riuscire prima che si riapra.
 */
const haDettoRallenta = (): boolean => api.ultimoEsitoRinnovo === 'rallenta';

/** Rinnova il token, ritentando a distanza crescente. `true` se ce l'ha fatta. */
async function rinnovaIdentita(): Promise<boolean> {
  if (await unRinnovo()) return true;
  for (const attesa of ATTESE_FRA_I_RINNOVI_MS) {
    if (haDettoRallenta()) {
      log.warn('Socket', 'il server ha chiesto di rallentare: non ritento il rinnovo');
      return false;
    }
    log.warn('Socket', 'rinnovo del token fallito, ritento', { fraMs: attesa });
    await attendi(attesa);
    if (await unRinnovo()) return true;
  }
  return false;
}

/*
 * Quanto si aspetta un rinnovo prima di rinunciarci. Un rinnovo che si pianta —
 * rete che non risponde e non fallisce — non deve bloccare niente: si prosegue
 * col token che c'e', anche morto, perche' meglio anonimi che scollegati (il
 * percorso `unauthenticated` del check-active recupera dopo).
 *
 * Lo usano TRE punti, e apposta con lo stesso simbolo invece che con tre numeri
 * da tenere allineati: lo sgancio del rinnovo condiviso qui sotto, il tetto
 * della callback di `auth` in `tokenPerHandshake`, e il tetto attorno al
 * rinnovo chiesto da `checkActiveGame`. Scadono insieme per costruzione, e
 * insieme entrano nel conto del budget di rientro.
 *
 * L'ultimo dei tre non era un di piu': `rinnovaIdentita` aspettava
 * `richiediTokenFresco()` senza tetto, e sotto c'e' un `fetch`. Una rete che
 * non risponde e non fallisce lasciava `checkActiveGame` appesa per sempre —
 * cioe' il vicolo cieco che questo ramo ripara, rientrato da un piano piu'
 * sotto.
 */
export const ATTESA_MASSIMA_RINNOVO_MS = 3_000;

/*
 * Il rinnovo in volo, condiviso. Il ciclo di riconnessione lo avvia presto
 * ('reconnect_attempt', appena socket.io decide di ritentare) e l'handshake lo
 * ASPETTA invece di avviarne un altro: senza questa condivisione la guardia
 * dell'intervallo minimo farebbe consegnare all'handshake il token vecchio
 * mentre il rinnovo giusto era gia' per strada.
 */
let rinnovoInCorso: Promise<void> | null = null;

/*
 * L'ultimo rinnovo chiesto dal ciclo di riconnessione. Tenuto separato da
 * quello di `checkActiveGame` di proposito: e' questo il percorso che salva la
 * partita, e un rinnovo tentato poco prima da un'altra strada non deve poterlo
 * zittire. Al massimo costa una chiamata in piu' ogni dieci secondi, e
 * `api.tryRefresh` deduplica comunque quelle contemporanee.
 */
let ultimoRinnovoPerRiconnessione = 0;

/**
 * Rinnova il token se e' scaduto. Non solleva mai e non fa mai attendere piu'
 * di un rinnovo per volta.
 *
 * Se il rinnovo fallisce si prosegue lo stesso, col token che c'e': meglio
 * anonimi che scollegati — il percorso `unauthenticated` di `checkActiveGame`
 * recupera l'identita' dopo.
 */
async function rinnovaTokenSeScaduto(): Promise<void> {
  /*
   * Niente token E nessuna identita' attesa: e' un visitatore, non c'e' niente
   * da salvare. Se invece lo store dice che siamo autenticati, il token va
   * rifatto proprio adesso -- vedi `ciSiAspettaUnIdentita`.
   */
  if (!ciSiAspettaUnIdentita()) return;
  // Ancora valido: non si tocca. Rinnovare a ogni blip di rete sarebbe una
  // richiesta HTTP per ogni riconnessione di ogni client, su un droplet da 1 vCPU.
  if (currentToken && userIdFromTokenNonScaduto(currentToken)) return;
  // Gia' per strada: si aspetta quello, non se ne avvia un secondo.
  if (rinnovoInCorso) return rinnovoInCorso;

  const adesso = Date.now();
  if (adesso - ultimoRinnovoPerRiconnessione < INTERVALLO_MINIMO_RINNOVO_MS) {
    /*
     * Socket.IO ritenta fino a 15 volte (`reconnectionAttempts`), fino a 3
     * secondi l'una (`reconnectionDelayMax`): senza questa guardia un token che
     * non si rinnova produrrebbe 15 rinnovi, cioe' 45 chiamate a
     * /api/auth/refresh. Con la guardia sono circa 12: i 15 tentativi coprono
     * ~40 secondi e la finestra e' di 10, quindi passano 4 rinnovi da 3
     * chiamate l'uno. Riduce di circa quattro volte, non di quindici — la
     * misura 3-contro-9 del test e' a orologio congelato e misura il
     * meccanismo, non il traffico.
     */
    log.warn('Socket', 'rinnovo gia\' tentato da poco, non insisto');
    return;
  }
  ultimoRinnovoPerRiconnessione = adesso;

  log.warn('Socket', 'token scaduto, lo rinnovo prima di riconnettere');
  const lavoro = (async () => {
    try {
      if (!(await rinnovaIdentita())) {
        log.warn('Socket', 'rinnovo fallito, si riconnette col token che c\'e\'');
      }
    } catch (errore) {
      log.warn('Socket', 'rinnovo esploso', { message: (errore as Error)?.message });
    }
  })();
  /*
   * Lo stesso tetto dell'handshake, e apposta lo STESSO simbolo: un rinnovo che
   * non si chiude mai — rete che non risponde e non fallisce — resterebbe qui
   * per tutta la vita della sessione, e ogni rinnovo successivo si accoderebbe a
   * lui invece di partire. In partita vorrebbe dire poter perdere per tutto il
   * resto della sessione: la stessa classe di guasto che questa riparazione
   * chiude, in scala ridotta. Scaduto il tetto lo si sgancia e il tentativo
   * dopo riparte pulito.
   */
  rinnovoInCorso = Promise.race([
    lavoro,
    attendi(ATTESA_MASSIMA_RINNOVO_MS).then(() => {
      log.warn('Socket', 'il rinnovo non si e\' chiuso entro il tetto, lo sgancio');
    }),
  ]);
  try {
    await rinnovoInCorso;
  } finally {
    rinnovoInCorso = null;
  }
}

/**
 * Consegna a socket.io il token per l'handshake, rinnovandolo prima se e'
 * scaduto.
 *
 * E' l'UNICA strada per cui un token arriva al server: il middleware lo legge
 * solo dall'handshake. Nella forma a funzione, `auth` viene valutata
 * nell'istante in cui il pacchetto CONNECT sta per partire e il pacchetto
 * ASPETTA la callback — quindi qui la corsa fra rinnovo e handshake non esiste.
 */
function tokenPerHandshake(consegnaA: (dati: object) => void): void {
  let consegnato = false;
  /* Inizializzato esplicitamente: `prefer-const` vede una sola assegnazione e
     chiede `const`, che qui non si puo' — il ramo del token valido esce prima
     di arrivare al `setTimeout`, quindi il tetto a volte non si alloca. */
  let tetto: ReturnType<typeof setTimeout> | undefined = undefined;
  handshakeInCorso = true;

  const consegna = (): void => {
    /*
     * Una consegna sola per handshake. Il tetto e la fine del rinnovo possono
     * arrivare a distanza di un'inezia: due consegne sarebbero due pacchetti
     * CONNECT, cioe' due socket lato server.
     */
    if (consegnato) return;
    consegnato = true;
    if (tetto !== undefined) clearTimeout(tetto);
    // `currentToken` si legge ADESSO, non all'ingresso: se il rinnovo e' andato
    // a buon fine, quello nuovo e' gia' li'.
    tokenConsegnatoAllHandshake = currentToken;
    try {
      consegnaA(currentToken ? { token: currentToken } : {});
    } finally {
      handshakeInCorso = false;
    }
  };

  /*
   * Caso normale — token valido, o nessun token da consegnare: si consegna
   * subito, senza rimandare di un microtask e senza allocare il tetto. E'
   * esattamente il comportamento sincrono di prima di questa riparazione.
   */
  /*
   * `!ciSiAspettaUnIdentita()` e non `!currentToken`: al boot un utente
   * registrato non ha il token in memoria (non e' persistito) ma un'identita'
   * ce l'ha, e consegnare subito «niente» lo faceva nascere anonimo. Ora si
   * aspetta il rinnovo, con lo stesso tetto degli altri percorsi.
   */
  if (!ciSiAspettaUnIdentita() || (currentToken && userIdFromTokenNonScaduto(currentToken))) {
    consegna();
    return;
  }

  /*
   * RIDONDANTE DI PROPOSITO — non toglierlo perche' "tanto non serve".
   * Da quando il rinnovo condiviso si sgancia da solo allo scadere dello stesso
   * tetto, `rinnovaTokenSeScaduto()` e' limitata su ogni suo percorso e la
   * consegna qui sotto arriverebbe comunque: nessun test muore se questo
   * `setTimeout` sparisce. Resta perche' la garanzia che regge tutto — *il
   * pacchetto CONNECT parte comunque* — dev'essere LOCALE a questa funzione e
   * non dipendere da un ragionamento su cosa fa un'altra. Basterebbe che
   * qualcuno aggiunga a `rinnovaTokenSeScaduto` un percorso non limitato per
   * riaprire il guasto in silenzio, e nessun test protesterebbe.
   */
  tetto = setTimeout(() => {
    log.warn('Socket', 'handshake: il rinnovo non risponde, connetto col token che c\'e\'');
    consegna();
  }, ATTESA_MASSIMA_RINNOVO_MS);

  void rinnovaTokenSeScaduto().then(consegna, consegna);
}

/**
 * Risolve `true` quando il server annuncia un'identita' NON anonima, `false` se
 * non arriva entro `ATTESA_IDENTITA_MS`. Il rinnovo del token fa riconnettere
 * (il server legge il token solo dall'handshake): ripetere la richiesta prima
 * dell'annuncio vorrebbe dire ripeterla ancora da anonimi.
 */
function attendiIdentitaAutenticata(): Promise<boolean> {
  if (connectedUserId) return Promise.resolve(true);
  const vivo = socket;
  if (!vivo) return Promise.resolve(false);
  return new Promise((resolve) => {
    let concluso = false;
    const chiudi = (esito: boolean): void => {
      if (concluso) return;
      concluso = true;
      clearTimeout(timer);
      vivo.off('auth:accepted', ascolta);
      resolve(esito);
    };
    const ascolta = ({ userId }: { userId: string | null }): void => {
      if (userId) chiudi(true);
    };
    const timer = setTimeout(() => chiudi(false), ATTESA_IDENTITA_MS);
    vivo.on('auth:accepted', ascolta);
  });
}

/**
 * `rinnovaIdentita` con un tetto sopra.
 *
 * Dentro c'e' `await richiediTokenFresco()`, e sotto di lui un `fetch`. Il
 * `fetch` ha il suo taglio (`TETTO_RICHIESTA_RINNOVO_MS`), ma il tetto qui
 * resta la difesa di questo percorso: e' cio' che garantisce che
 * `checkActiveGame` si CHIUDA — con un esito qualunque — invece di restare
 * appesa. Una `checkActiveGame` mai risolta non fa girare
 * `applyReconnectResult`: il giocatore resta su una damiera viva dove le mosse
 * non arrivano e la resa e' muta, mentre il grace scade e la partita e' persa.
 *
 * Scaduto il tetto si prosegue come se il rinnovo fosse fallito: e' la stessa
 * scelta del rinnovo d'handshake — meglio anonimi che appesi.
 */
async function rinnovaIdentitaConTetto(): Promise<boolean> {
  return Promise.race([
    rinnovaIdentita().catch((errore) => {
      log.warn('Socket', 'rinnovo esploso durante il check-active', { message: (errore as Error)?.message });
      return false;
    }),
    attendi(ATTESA_MASSIMA_RINNOVO_MS).then(() => {
      log.warn('Socket', 'il rinnovo del check-active non si e\' chiuso entro il tetto, rinuncio');
      return false;
    }),
  ]);
}

/**
 * Il caso peggiore composto di UN RIENTRO, calcolato dalle costanti che lo
 * compongono invece che a mano. Non e' il caso peggiore di `checkActiveGame`:
 * e' quello del percorso intero, perche' e' il percorso intero a consumare il
 * grace period, e sorvegliarne un segmento solo lascerebbe crescere gli altri.
 *
 *   1. il rinnovo del token PRIMA dell'handshake          ATTESA_MASSIMA_RINNOVO_MS
 *      (`rinnovaTokenSeScaduto`, che il ciclo di
 *      riconnessione avvia prima di ritentare)
 *   2. primo `game:check-active` che scade                ATTESA_ACK_CHECK_ACTIVE_MS
 *   3. ripetizione che risponde all'ultimo istante        ATTESA_ACK_CHECK_ACTIVE_MS
 *      (se scadesse anche lei si tornerebbe subito: il
 *      percorso di rinnovo parte solo da una RISPOSTA)
 *   4. il rinnovo chiesto da `checkActiveGame`            ATTESA_MASSIMA_RINNOVO_MS
 *      (limitato dallo stesso tetto, quindi e' LUI il
 *      confine, non la somma dei suoi tentativi)
 *   5. l'attesa dell'annuncio d'identita'                 ATTESA_IDENTITA_MS
 *   6. la ripetizione finale, che puo' scadere            ATTESA_ACK_CHECK_ACTIVE_MS
 *
 * Serve a rendere VERIFICABILE cio' che altrimenti sarebbe un'affermazione in
 * un commento: un test confronta questo totale con `GRACE_PERIOD_MS`, e se
 * qualcuno tocca uno qualunque di quei numeri e' il test a fermarlo.
 */
export const ATTESA_MASSIMA_RIENTRO_MS =
  ATTESA_MASSIMA_RINNOVO_MS * 2
  + ATTESA_ACK_CHECK_ACTIVE_MS * 3
  + ATTESA_IDENTITA_MS;

/**
 * Quanta parte del grace period del server un rientro puo' spendere a
 * insistere.
 *
 * La finestra scorre dal SCOLLEGAMENTO, ma il recupero lato client comincia
 * solo quando la rete e' tornata: il tempo prima di quel momento non e' nostro
 * da spendere ne' da recuperare, e trattarlo come se lo fosse porterebbe a
 * tetti cosi' corti da rilasciare partite vive — cioe' a causare il guasto che
 * si vuole evitare. Due terzi scarsi alle attese, e il 35% che resta e' margine
 * vero: e' il tempo che serve al server per confermare il rientro dopo che
 * `game:check-active` ha risposto.
 */
export const FRAZIONE_DI_GRACE_CONCESSA = 0.65;

/**
 * Chiede al server se abbiamo una partita in corso.
 *
 * Se non risponde affatto entro `ATTESA_ACK_CHECK_ACTIVE_MS` si ripete la
 * domanda una volta sola, poi si restituisce `timedOut` — che il chiamante puo'
 * distinguere da "non hai partite".
 *
 * Se risponde `unauthenticated` non ha potuto guardare: si rinnova il token
 * (ritentando, il guasto e' spesso transitorio) e si richiede UNA volta sola.
 * Mai in ciclo — la seconda risposta si restituisce com'e', anche se anonima
 * anche lei.
 *
 * Una risposta `unauthenticated` che esce di qui vuol dire che il recupero e'
 * gia' stato tentato e non e' riuscito: per il chiamante non c'e' piu' niente da
 * aspettare, e trattarla come "nessuna partita" (rilasciare e tornare in lobby)
 * e' esattamente quello che faceva prima di questa riparazione. Aggrapparsi alla
 * partita, invece, lascerebbe una damiera viva su cui non si puo' ne' muovere ne'
 * abbandonare.
 */
export async function checkActiveGame(): Promise<CheckActiveGameResponse> {
  let risposta = await emettiCheckActive();

  /*
   * Scaduto senza ack: si ripete UNA volta sola, e senza rinnovare niente —
   * non e' un problema d'identita', la domanda non e' proprio arrivata a
   * destinazione (o la risposta non e' tornata). Se anche la ripetizione scade,
   * l'esito `timedOut` esce di qui com'e': il chiamante sa che il server non ha
   * guardato, e non ci sono altri colpi da tentare. Mai in ciclo: due ack persi
   * di fila vogliono dire server irraggiungibile, e insistere lo peggiora.
   */
  if (risposta.timedOut) {
    log.warn('Socket', 'check-active scaduto senza risposta, ripeto una volta sola');
    risposta = await emettiCheckActive();
  }

  if (!risposta.unauthenticated) return risposta;

  const adesso = Date.now();
  if (adesso - ultimoRinnovoPerCheckActive < INTERVALLO_MINIMO_RINNOVO_MS) {
    log.warn('Socket', 'check-active anonimo: rinnovo gia\' tentato da poco, non insisto');
    return risposta;
  }
  ultimoRinnovoPerCheckActive = adesso;

  log.warn('Socket', 'check-active: il server non sa chi siamo, rinnovo il token e richiedo');
  if (!(await rinnovaIdentitaConTetto())) {
    log.warn('Socket', 'check-active anonimo: rinnovo fallito o scaduto, la partita si rilascia');
    return risposta;
  }
  if (!(await attendiIdentitaAutenticata())) {
    log.warn('Socket', 'check-active anonimo: identita\' mai annunciata, la partita si rilascia');
    return risposta;
  }
  return emettiCheckActive();
}

/**
 * Sveglia il socket al ritorno in primo piano.
 *
 * Al rientro non c'era nessuno a dire «riprova adesso»: si aspettava che il
 * timer di ping di engine.io si sbloccasse e che il backoff (1→3 s, quindici
 * tentativi) facesse il suo giro. Su un grace period di 30 secondi quel budget
 * è già contato al centesimo, e ogni secondo bruciato ad aspettare è tolto da
 * lì — cioè è tempo in cui si perde per disconnessione una partita che si
 * poteva ancora giocare.
 *
 * Tre casi, e sono diversi:
 *
 *  1. **socket giù** → si riconnette subito, senza aspettare il backoff.
 *     Questo copre anche `reconnect_failed`, che oggi è un vicolo cieco: dopo
 *     i quindici tentativi l'app resta morta fino a un ricarico a mano.
 *  2. **socket "connesso" ma morto** — il caso iOS: il WebSocket muore senza
 *     evento di chiusura, `socket.connected` resta `true` e nessuna
 *     riconnessione parte finché il ping timer non scade. Si sonda con
 *     `game:check-active`, che ha già un tetto d'ack di 3 s calibrato sul
 *     budget di rientro; se non risponde, il socket è uno zombie e lo si
 *     abbatte a mano.
 *  3. **socket vivo** → la sonda risponde, e con la risposta arriva **anche il
 *     resync completo** (scacchiera, orologio, conversazione) attraverso
 *     `applyReconnectResult`. Per questo non serve nessun evento nuovo: bastava
 *     innescare quelli che già ci sono.
 *
 * ⚠️ Il fermo per eviction (`newSessionEvicted`) NON si scavalca: quella scheda
 * è stata messa giù apposta perché lo stesso account ha aperto una sessione
 * altrove, e risvegliarla riaprirebbe il ping-pong che il fermo esiste per
 * chiudere.
 *
 * ⚠️ E non si tocca nessun orologio: svegliare il socket serve a rientrare al
 * tavolo, non a recuperare il tempo passato in secondo piano. Quello è corso, e
 * doveva correre.
 */
export async function risvegliaSocket(): Promise<void> {
  if (!socket) return;
  if (newSessionEvicted) {
    log.info('Socket', 'risveglio saltato: sessione evinta da un altro accesso');
    return;
  }
  /*
   * Un handshake gia' in volo NON si scavalca.
   *
   * `handshakeInCorso` esiste esattamente per questo: con la callback di `auth`
   * ancora aperta — e puo' restarlo fino a `ATTESA_MASSIMA_RINNOVO_MS` mentre
   * rinnova il token — un `connect()` manda un SECONDO pacchetto CONNECT, cioe'
   * due socket lato server, `connection.handler` due volte e l'eviction
   * same-user contro la sessione appena nata. Il ritorno in primo piano puo'
   * benissimo cadere dentro quella finestra.
   */
  if (handshakeInCorso) {
    log.info('Socket', 'risveglio saltato: handshake gia in volo');
    return;
  }
  /*
   * E nemmeno un fermo VOLUTO. `force-disconnect` con un motivo diverso da
   * 'new-session' — per esempio un account cancellato — spegne `reconnection`
   * senza alzare `newSessionEvicted`: rimetterlo a `true` qui riporterebbe in
   * vita una connessione che il server ha chiuso apposta.
   */
  if (socket.io.opts.reconnection === false) {
    log.info('Socket', 'risveglio saltato: la riconnessione e stata spenta di proposito');
    return;
  }

  if (!socket.connected) {
    log.info('Socket', 'risveglio: socket giu, riconnetto subito');
    socket.connect();
    return;
  }

  /*
   * Sonda — e messaggero.
   *
   * Il rientro vero lo fa il SERVER dietro `game:check-active` (`socket.join`,
   * `tentativeReconnect`, il riaggancio all'arena), e `timedOut` distingue un
   * socket vivo da uno zombie. Ma la risposta porta anche l'ESITO, e qui non
   * c'e' nessun altro che lo raccolga: `applyReconnectResult` sta sul percorso
   * di `onConnect`, che su questo ramo — socket mai caduto — non passa. Finche'
   * la si buttava via, chi rientrava su una partita finita mentre era via
   * restava su una damiera viva dove nessun gesto faceva niente.
   *
   * Una risposta `unauthenticated` NON si consegna grezza: vuol dire «non ho
   * potuto guardare», e lo store la leggerebbe come «nessuna partita»
   * rilasciando una partita viva. Si passa da `checkActiveGame`, che rinnova
   * l'identita' e richiede.
   */
  const risposta = await emettiCheckActive();
  if (!risposta.timedOut) {
    callbacks.onRientro?.(risposta.unauthenticated ? await checkActiveGame() : risposta);
    return;
  }

  log.warn('Socket', 'risveglio: socket connesso ma muto, lo abbatto e riconnetto');
  socket.disconnect();
  socket.connect();
}

/**
 * Send a move to the server
 */
export function sendMove(roomId: string, move: Move): void {
  if (!socket?.connected) {
    log.error('Socket', 'Cannot send move: not connected — optimistic board already applied, no rollback → desync', { roomId, from: move.from, to: move.to });
    return;
  }

  // Normalize captures to square numbers (server Zod schema expects number[])
  const normalizedMove = {
    from: move.from,
    to: move.to,
    captures: move.captures?.map((c) => typeof c === 'number' ? c : c.square) ?? [],
  };

  log.info('Socket', 'emit game:move', { roomId, from: move.from, to: move.to });
  socket.emit('game:move', { roomId, move: normalizedMove });
}

/**
 * Quanto si aspetta l'ack dell'abbandono.
 *
 * Serve soprattutto contro un SERVER vecchio: durante un rilascio graduale
 * (o con una PWA installata che parla con un backend non ancora aggiornato)
 * l'handler d'abbandono non ha la callback, non risponde mai, e senza tetto la
 * promessa resterebbe appesa per sempre. Un ack mancato non e' un guasto: in
 * quel caso l'abbandono e' partito lo stesso e la fine partita arrivera' da
 * `game:end`.
 */
export const ATTESA_ACK_ABBANDONO_MS = 5_000;

/** La risposta del server all'abbandono. Vedi `RESIGN_*` in @draughts/shared. */
export interface RispostaAbbandono {
  success?: true;
  error?: string;
  /** Il resoconto della partita gia' finita, nella forma di `check-active`. */
  ended?: CheckActiveGameResponse;
  /** Nessuna risposta entro il tetto: non e' un errore, e' un silenzio. */
  timedOut?: true;
}

/**
 * Abbandona la partita — e riferisce com'e' andata.
 *
 * Prima usciva muta a socket giu' (nemmeno una riga di log) e non chiedeva
 * nessun ack: se la stanza non c'era piu', o se la partita era finita mentre si
 * era in secondo piano, il server taceva e chi aveva premuto «Abbandona» non
 * vedeva succedere niente. Era meta' del difetto; l'altra meta' era lo store,
 * che usciva in silenzio fuori dallo stato 'playing'.
 */
export function resignGame(roomId: string): Promise<RispostaAbbandono> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      log.warn('Socket', 'game:resign non spedito: socket non connesso', { roomId });
      resolve({ error: 'NOT_CONNECTED' });
      return;
    }

    let concluso = false;
    const concludi = (risposta: RispostaAbbandono): void => {
      if (concluso) return;
      concluso = true;
      clearTimeout(timer);
      resolve(risposta);
    };
    const timer = setTimeout(() => {
      log.warn('Socket', 'game:resign: nessun ack entro il tetto', { roomId, entroMs: ATTESA_ACK_ABBANDONO_MS });
      concludi({ timedOut: true });
    }, ATTESA_ACK_ABBANDONO_MS);

    log.info('Socket', 'emit game:resign', { roomId });
    socket.emit('game:resign', roomId, (risposta: RispostaAbbandono) => {
      if (risposta?.error) log.warn('Socket', 'game:resign rifiutato', { roomId, error: risposta.error });
      concludi(risposta ?? {});
    });
  });
}

/**
 * Send a chat message
 */
export function sendGameChat(roomId: string, message: string): void {
  if (!socket?.connected) {
    return;
  }

  log.info('Socket', 'emit game:chat', { roomId });
  socket.emit('game:chat', { roomId, message });
}

/**
 * Tetto d'attesa di default per un ack di richiesta-risposta.
 *
 * Nessun emit dell'app usava `.timeout()` di socket.io-client, e le opzioni di
 * `io()` non hanno `ackTimeout`: ventun funzioni costruivano una promessa
 * attorno a un ack che, se non arrivava, la lasciava appesa PER SEMPRE. Non
 * serve uno zombie per innescarlo — socket.io non rifiuta gli ack pendenti alla
 * disconnessione, quindi basta un blip di rete un attimo dopo il tocco. Il
 * blocco si vedeva a schermo: `lobby.setBusy(true)` senza il suo `finally`
 * lascia i bottoni di cadenza e «Annulla» disabilitati, con `error` a null,
 * cioè nessun messaggio — e nessun altro punto del sorgente riazzera `isBusy`.
 *
 * OTTO secondi, non tre: la scadenza qui non è una resa, è un errore mostrato a
 * chi ha premuto, e un falso fallimento su un droplet a 1 vCPU è peggio di uno
 * spinner lento. Alcune di queste richieste fanno più giri al database prima di
 * rispondere (`lobby:quick` accoppia, `lobby:sit` apre una stanza). Dove un
 * tetto più stretto era già stato scelto con cura resta quello:
 * `game:check-active` 3 s (vincolato dal grace period, con un test che lo
 * sorveglia), gli eventi d'arena 5 s, `game:resign` il suo.
 */
export const TETTO_ACK_MS = 8_000;

/** L'ack non è arrivato entro il tetto. Porta il nome dell'evento, per i log. */
export class AckTimeoutError extends Error {
  constructor(public readonly evento: string) {
    super(`No server acknowledgement for ${evento}`);
    this.name = 'AckTimeoutError';
  }
}

/**
 * Avvolge un ack con un tetto di tempo: restituisce il callback da passare a
 * `socket.emit`. Chi arriva primo vince, l'altro non fa niente.
 *
 * Volutamente un involucro sul CALLBACK e non sulla promessa: ogni chiamante ha
 * la sua traduzione dell'errore (chi rifiuta, chi risolve con un codice, chi
 * controlla una generazione per scartare gli ack stantii), e riscrivere ventun
 * corpi avrebbe messo a rischio quelle differenze per aggiungere una riga.
 */
function conTetto<T>(
  evento: string,
  onAck: (risposta: T) => void,
  onScaduto: () => void,
  tetto: number = TETTO_ACK_MS,
): (risposta: T) => void {
  let concluso = false;
  const timer = setTimeout(() => {
    if (concluso) return;
    concluso = true;
    log.warn('Socket', 'ack scaduto senza risposta del server', { evento, tetto });
    onScaduto();
  }, tetto);
  return (risposta: T) => {
    if (concluso) return;
    concluso = true;
    clearTimeout(timer);
    onAck(risposta);
  };
}

/** Snapshot of the open tables, the counters, and our own table if we have one. */
export function listLobby(): Promise<LobbySnapshot> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }
    socket.emit('lobby:list', conTetto<LobbySnapshot>('lobby:list', resolve, () => reject(new AckTimeoutError('lobby:list'))));
  });
}

/**
 * Press one of the quick cadence buttons: sit at the oldest matching table, or
 * publish one and wait. The ack says which of the two happened.
 */
export function quickPlay(variant: string, timeControl: TimeControlPreset): Promise<LobbyQuickResponse> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      log.warn('Socket', 'quickPlay: not connected');
      reject(new Error('Not connected to server'));
      return;
    }
    log.info('Socket', 'emit lobby:quick', { variant, timeControl });
    socket.emit('lobby:quick', { variant, timeControl }, conTetto<LobbyQuickResponse | LobbyError>(
      'lobby:quick',
      (response) => {
        if ('code' in response) {
          log.warn('Socket', 'lobby:quick rejected', { code: response.code });
          reject(new LobbyRequestError(response.code, response.message));
        } else {
          resolve(response);
        }
      },
      () => reject(new AckTimeoutError('lobby:quick')),
    ));
  });
}

/** Open a table with the full option set (the custom-game form). */
export function createLobbyTable(settings: ChallengeSettings): Promise<{ tableId: string }> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }
    log.info('Socket', 'emit lobby:create', { variant: settings.variant, timeControl: settings.timeControl });
    socket.emit('lobby:create', settings, conTetto<{ tableId: string } | LobbyError>(
      'lobby:create',
      (response) => {
        if ('code' in response) {
          log.warn('Socket', 'lobby:create rejected', { code: response.code });
          reject(new LobbyRequestError(response.code, response.message));
        } else {
          resolve(response);
        }
      },
      () => reject(new AckTimeoutError('lobby:create')),
    ));
  });
}

/** Sit at a specific table picked from the list. */
export function sitAtTable(tableId: string): Promise<LobbySitResponse> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }
    log.info('Socket', 'emit lobby:sit', { tableId });
    socket.emit('lobby:sit', { tableId }, conTetto<LobbySitResponse | LobbyError>(
      'lobby:sit',
      (response) => {
        if ('code' in response) {
          log.warn('Socket', 'lobby:sit rejected', { code: response.code });
          reject(new LobbyRequestError(response.code, response.message));
        } else {
          resolve(response);
        }
      },
      () => reject(new AckTimeoutError('lobby:sit')),
    ));
  });
}

/** Leave the queue. Fire-and-forget: the removal broadcast is the real answer. */
export function cancelLobbyTable(tableId?: string): void {
  if (!socket?.connected) return;
  log.info('Socket', 'emit lobby:cancel', { tableId });
  socket.emit('lobby:cancel', tableId ? { tableId } : {}, () => {});
}

/**
 * Join tournament via WebSocket
 */
export function joinTournamentWs(
  tournamentId: string,
  credential?: { password?: string; token?: string },
): Promise<TournamentJoinedData> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    // La password non entra nei log, mai. Solo il fatto che ce n'era una.
    log.info('Socket', 'emit tournament:join', { tournamentId, conCredenziale: !!credential });
    socket.emit(
      'tournament:join',
      { tournamentId, ...(credential ?? {}) },
      conTetto<TournamentJoinedData | TournamentError>(
        'tournament:join',
        (response) => {
          if ('code' in response) {
            /*
             * Il CODICE, non solo la frase. La frase inglese del server non
             * corrisponde a nessuna chiave i18n e finiva sempre nel ripiego
             * «errore sconosciuto»: cosi' invece «serve la password» arriva a
             * schermo tradotto, che e' il patto descritto in
             * `tournament-error-key.ts`.
             */
            reject(Object.assign(new Error(response.message), { code: response.code }));
          } else {
            resolve(response);
          }
        },
        () => reject(new AckTimeoutError('tournament:join')),
      ),
    );
  });
}

/**
 * Leave tournament
 */
export function leaveTournamentWs(tournamentId?: string): void {
  if (!socket?.connected) {
    return;
  }
  log.info('Socket', 'emit tournament:leave', { tournamentId });
  // L'id nel payload: il server lo usa quando le sue mappe per `socket.id` non
  // conoscono questa connessione — dopo una ricarica di pagina, per esempio —
  // e senza di esso l'azione finiva in un ramo muto che non toccava il database.
  socket.emit('tournament:leave', tournamentId ? { tournamentId } : undefined);
}

/**
 * Watch a tournament room for real-time updates (without joining)
 */
/**
 * Il torneo che stiamo guardando, ricordato per poterci RIENTRARE.
 *
 * Le stanze di Socket.IO appartengono alla singola connessione: dopo una caduta
 * di rete il socket nuovo non e' piu' dentro `tournament:<id>`, e nessuno ce lo
 * rimetteva — ne' il client ne' il gestore della connessione, che i tornei non
 * li nomina. Da li' in poi la classifica si congelava e fine turno, fine torneo
 * e fine partita non arrivavano piu', senza un indizio a schermo. L'arena questo
 * riaggancio ce l'ha da tempo, con la stessa nota.
 *
 * Si scrive prima di controllare la connessione, cosi' anche un watch tentato da
 * offline viene ripreso appena il socket torna.
 */
let torneoGuardatoId: string | null = null;

/** Rientra nella stanza del torneo dopo che il socket e' tornato. */
function riagganciaTorneo(): void {
  const tournamentId = torneoGuardatoId;
  if (!tournamentId || !socket) return;
  log.info('Socket', 'riaggancio del torneo dopo la riconnessione', { tournamentId });
  socket.emit('tournament:watch', { tournamentId });
}

export function watchTournament(tournamentId: string): void {
  torneoGuardatoId = tournamentId;
  if (!socket?.connected) return;
  log.info('Socket', 'emit tournament:watch', { tournamentId });
  socket.emit('tournament:watch', { tournamentId });
}

/**
 * Stop watching a tournament room
 */
export function unwatchTournament(tournamentId: string): void {
  if (torneoGuardatoId === tournamentId) torneoGuardatoId = null;
  if (!socket?.connected) return;
  log.info('Socket', 'emit tournament:unwatch', { tournamentId });
  socket.emit('tournament:unwatch', { tournamentId });
}

// ========== Arena ==========

/**
 * Join arena via WebSocket
 */
export function joinArenaWs(
  arenaId: string,
  /**
   * La chiave di un'arena privata: la password detta a voce, o il gettone che
   * arriva col link.
   *
   * Si manda solo alla PRIMA iscrizione. Il server la chiede solo quando deve
   * creare la riga di partecipante: i ri-emit del riarmo e della sonda di
   * presenza passano di qui senza, ed e' voluto — pretenderla li' butterebbe
   * fuori dall'arena privata chi ci e' dentro, al primo sbalzo di rete.
   */
  credential?: { password?: string; token?: string },
): Promise<ArenaJoinedData> {
  return emitArenaWithAck('arena:join', { arenaId, ...(credential ? { credential } : {}) });
}

/*
 * ── Uscire, fermarsi, ripartire ───────────────────────────────────────────
 *
 * Tutte e tre tornano `false` quando il socket e' giu' e l'evento NON e'
 * partito. Prima uscivano in silenzio, e chi le chiama aveva gia' riscritto lo
 * stato in modo ottimistico: con la rete caduta per due secondi (WiFi→4G) si
 * premeva «Esci» e lo schermo diceva «non iscritto» mentre a database si era
 * ancora dentro e disponibili -- al rientro il riarmo rimandava `arena:join` e
 * ci si ritrovava in coda in un'arena che si credeva lasciata. Con «Pausa» era
 * peggio: schermo «in pausa», server che continua ad accoppiare.
 *
 * `joinArenaWs` non ha questo problema perche' passa da `emitArenaWithAck`, che
 * ha ack, tetto d'attesa e codice d'errore.
 */

/**
 * Leave arena. `false` se il socket e' giu': l'evento non e' partito.
 */
export function leaveArenaWs(arenaId?: string): boolean {
  if (!socket?.connected) return false;
  log.info('Socket', 'emit arena:leave', { arenaId });
  socket.emit('arena:leave', { arenaId });
  return true;
}

/**
 * Pause in arena (stop receiving pairings). `false` se non e' partito.
 */
export function pauseArena(arenaId?: string): boolean {
  if (!socket?.connected) return false;
  log.info('Socket', 'emit arena:pause', { arenaId });
  socket.emit('arena:pause', { arenaId });
  return true;
}

/**
 * Resume in arena. `false` se non e' partito.
 */
export function resumeArena(arenaId?: string): boolean {
  if (!socket?.connected) return false;
  log.info('Socket', 'emit arena:resume', { arenaId });
  socket.emit('arena:resume', { arenaId });
  return true;
}

/**
 * ── Dichiarare, non decidere ──────────────────────────────────────────────
 *
 * `arena:away` e `arena:back` dicono al server «non sto guardando» / «sono
 * tornato», e nient'altro: l'attesa e la pausa vera le fa il server, dentro il
 * giro di accoppiamento. E' il rovescio di come funzionava prima, quando a
 * decidere era un `setTimeout` da cinque secondi nel client — che su iOS non
 * scattava affatto (la pagina viene sospesa prima) e su Android non veniva
 * nemmeno armato se l'app spariva mentre si stava giocando.
 *
 * Qui il `false` da socket giu' NON richiede rimedio, al contrario di pausa,
 * ripresa e uscita: non c'e' nessuno stato ottimistico da riallineare, e il
 * caso «socket caduto mentre si e' assenti» resta coperto dal server, che tiene
 * l'assenza gia' dichiarata anche dopo la morte del socket.
 */

/**
 * L'arena di cui questa sessione dichiara la presenza.
 *
 * Si scrive anche quando l'emit NON parte, ed e' tutto il punto: al ritorno in
 * primo piano il socket e' morto molto piu' spesso di quanto sia vivo, quindi
 * il `false` di `arenaBackWs` e' il caso normale, non l'eccezione.
 */
let arenaPresenzaId: string | null = null;
/** Il socket su cui la presenza e' gia' stata ridichiarata dopo la riconnessione. */
let arenaPresenzaRidichiarataSu: string | null = null;

/** «Non sto guardando». `false` se non e' partito. */
export function arenaAwayWs(arenaId?: string): boolean {
  if (arenaId) arenaPresenzaId = arenaId;
  if (!socket?.connected) return false;
  log.info('Socket', 'emit arena:away', { arenaId });
  socket.emit('arena:away', { arenaId });
  return true;
}

/** «Sono tornato». `false` se non e' partito. */
export function arenaBackWs(arenaId?: string): boolean {
  if (arenaId) arenaPresenzaId = arenaId;
  if (!socket?.connected) return false;
  log.info('Socket', 'emit arena:back', { arenaId });
  socket.emit('arena:back', { arenaId });
  return true;
}

/** Non si e' piu' iscritti: non c'e' piu' niente da ridichiarare. */
export function dimenticaPresenzaArena(): void {
  arenaPresenzaId = null;
  arenaPresenzaRidichiarataSu = null;
}

/**
 * Ridichiara la presenza in arena appena il socket torna.
 *
 * ⚠️ Questa e' la meta' che mancava. `arena:away` e' durevole lato server —
 * l'assenza resta scritta anche dopo la morte del socket che l'ha dichiarata —
 * mentre `arena:back` no: se al ritorno in primo piano il socket e' giu',
 * `arenaBackWs` esce con `false` e **nessuno ripete**. Il giocatore resta
 * assente, il setaccio lo tiene in pausa e non viene piu' accoppiato; e siccome
 * e' proprio `handleArenaBack` a riannunciare `arena:paused`, il client non
 * riceve nemmeno la notizia della pausa e continua a mostrarsi «in ricerca».
 * Le due verita' divergono e non si riallineano piu' da sole.
 *
 * Lo stato non si prende dalla memoria ma dalla PAGINA: fra la dichiarazione
 * persa e la riconnessione la visibilita' puo' essere cambiata, e quel che
 * conta e' com'e' adesso. Chi e' ancora nascosto ridichiara l'assenza, cosi'
 * l'attesa riparte sul socket vivo invece di restare appesa a uno morto.
 *
 * Non rientra in coda da solo: `arena:back` cancella l'assenza e riannuncia la
 * pausa, la ripresa resta un gesto esplicito («Riprendi»).
 */
function ridichiaraPresenzaArena(): void {
  const arenaId = arenaPresenzaId;
  if (!arenaId || !socket?.connected) return;
  if (arenaPresenzaRidichiarataSu === socket.id) return;
  arenaPresenzaRidichiarataSu = socket.id ?? null;
  const nascosta = paginaNascosta();
  log.info('Socket', 'ridichiaro la presenza in arena dopo la riconnessione', { arenaId, nascosta });
  socket.emit(nascosta ? 'arena:away' : 'arena:back', { arenaId });
}

/**
 * L'arena che questa connessione sta seguendo.
 *
 * Come per lo spettatore qui sotto: le stanze di Socket.IO appartengono alla
 * singola connessione, e dopo una riconnessione `arena:<id>` non contiene più
 * nessuno — l'annuncio di partenza, le classifiche e la fine smettono di
 * arrivare senza che niente lo dica. Il rientro sta in questo modulo e non in
 * uno store perché il registro dei callback è unito per chiave: uno store che
 * registrasse il proprio `onConnect` cancellerebbe quello del multiplayer, che
 * è l'unico a ricontrollare se c'è una partita da riprendere.
 *
 * Si scrive prima di controllare la connessione, così anche un watch tentato
 * da offline viene ripreso appena il socket torna.
 */
let watchedArenaId: string | null = null;

/** Rientra nella stanza dell'arena dopo che il socket è tornato. */
function rearmArena(): void {
  const arenaId = watchedArenaId;
  if (!arenaId || !socket) return;
  log.info('Socket', 're-arming arena after reconnect', { arenaId });
  socket.emit('arena:watch', { arenaId });
  callbacks.onArenaRewatch?.({ arenaId });
}

/**
 * Watch an arena room for real-time updates (without joining)
 */
export function watchArena(arenaId: string): void {
  watchedArenaId = arenaId;
  if (!socket?.connected) return;
  log.info('Socket', 'emit arena:watch', { arenaId });
  socket.emit('arena:watch', { arenaId });
}

/**
 * Stop watching an arena room
 */
export function unwatchArena(arenaId: string): void {
  if (watchedArenaId === arenaId) watchedArenaId = null;
  if (!socket?.connected) return;
  log.info('Socket', 'emit arena:unwatch', { arenaId });
  socket.emit('arena:unwatch', { arenaId });
}

/**
 * L'errore che arriva dall'arena, col suo codice.
 *
 * Un `new Error(response.message)` teneva il pezzo che non serve — una frase
 * inglese scritta per i log del server — e buttava via il `code`, che è l'unica
 * parte su cui l'interfaccia possa decidere qualcosa. Qui il codice resta:
 * `TOO_LATE` non è `INTERNAL_ERROR`, e il bottone risponde in modo diverso.
 *
 * `message` sopravvive per i log, ma non va mai mostrato a schermo: non è
 * tradotto e non è scritto per chi gioca.
 */
export class ArenaSocketError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'ArenaSocketError';
  }
}

/**
 * Quanto si aspetta la risposta del server prima di dichiarare persa la
 * richiesta. Senza, un ack che non arriva mai (socket caduto a metà, server che
 * non risponde) lascia la promessa appesa per sempre: chi ha premuto non vede
 * né esito né errore.
 */
const ARENA_ACK_TIMEOUT_MS = 5000;

/** Emette con ack, un timeout e l'errore che porta il suo codice. */
/* `unknown` e non `string`: da quando un'arena puo' essere privata, `arena:join`
   porta anche una credenziale, che e' un oggetto. */
function emitArenaWithAck<T>(event: string, payload: Record<string, unknown>): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new ArenaSocketError('NOT_CONNECTED', 'Not connected to server'));
      return;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      log.warn('Socket', `${event} timed out`, { timeoutMs: ARENA_ACK_TIMEOUT_MS });
      reject(new ArenaSocketError('TIMEOUT', `${event} timed out`));
    }, ARENA_ACK_TIMEOUT_MS);

    log.info('Socket', `emit ${event}`, payload);
    socket.emit(event, payload, (response: T | { code: string; message: string }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (response && typeof response === 'object' && 'code' in response) {
        reject(new ArenaSocketError(response.code, response.message));
      } else {
        resolve(response as T);
      }
    });
  });
}

/**
 * Il boost dell'arena: paghi tempo per il doppio dei punti se vinci. Non è
 * «metà orologio» — il prezzo lo calcola il server (`GameRoom.applyBoost`):
 * l'incremento sempre, il tempo base solo dove vale almeno quaranta
 * incrementi. `newTime` è l'orologio che ne esce, e va scritto com'è.
 */
export function boostArena(gameId: string): Promise<{ success: boolean; newTime?: number }> {
  return emitArenaWithAck('arena:boost', { gameId });
}

export function unboostArena(gameId: string): Promise<{ success: boolean; newTime?: number }> {
  return emitArenaWithAck('arena:unboost', { gameId });
}

/**
 * Disconnect socket
 */
export function disconnectSocket(): void {
  // An explicit disconnect (logout / identity change) is a deliberate session reset,
  // so lift any new-session suppression — the next connect() is intentional.
  impostaSessioneAltrove(false);
  /*
   * Un rinnovo ancora appeso appartiene alla sessione che si sta chiudendo:
   * lasciarlo in `rinnovoInCorso` farebbe aspettare l'handshake successivo, che
   * e' di un'altra identita'.
   */
  rinnovoInCorso = null;
  tokenConsegnatoAllHandshake = null;
  handshakeInCorso = false;
  if (socket) {
    log.info('Socket', 'disconnectSocket called');
    socket.disconnect();
    socket = null;
    connectCallbackCalled = false;
    hasBeenConnected = false;
  }
}

// ========== Online Users ==========

/** Quanto si aspetta l'ack della lista dei collegati. */
export const ATTESA_ACK_LISTA_ONLINE_MS = 5_000;

/**
 * L'errore della lista dei collegati, col suo CODICE.
 *
 * Prima era `new Error('Not connected to server')`, e `OnlinePlayersList`
 * stampava `err.message`: una frase inglese scritta per i log finiva sotto gli
 * occhi di chi aveva soltanto l'app tornata dal secondo piano, in qualunque
 * lingua parlasse il resto della pagina. Col codice l'interfaccia puo' scegliere
 * una traduzione — e puo' distinguere «mi sto ricollegando» da «e' andato
 * storto qualcosa», che sono due cose diverse.
 */
/** Quale punteggio si chiede: vedi utils/rating-bucket. */
export interface RatingBucket {
  variant: string;
  cadence: 'bullet' | 'blitz' | 'rapid' | 'classical';
}

export interface RispostaListaOnline {
  users: OnlineUser[];
  count: number;
  counts: LobbyCounts;
  /** Il bucket che il server ha DAVVERO onorato: assente se non ne ha onorato nessuno. */
  bucket?: RatingBucket;
}

export class ErroreListaOnline extends Error {
  constructor(public readonly code: 'NOT_CONNECTED' | 'TIMEOUT', message: string) {
    super(message);
    this.name = 'ErroreListaOnline';
  }
}

/**
 * La lista dei collegati.
 *
 * ⚠️ Il tetto d'attesa non e' un lusso: un socket puo' risultare `connected` con
 * il transport gia' morto — il caso tipico subito dopo un risveglio — e allora
 * l'ack non arriva mai. Senza tetto la promessa non si risolve, il `finally` di
 * chi chiama non parte e lo spinner «Caricamento giocatori…» resta acceso per
 * sempre.
 */
/**
 * Un server che il bucket non lo conosce.
 *
 * Succede in una finestra sola — bundle nuovo in cache, server tornato
 * indietro — ma li' l'handler vecchio scambia l'oggetto per il callback e non
 * risponde affatto: il pannello resterebbe sul tetto d'attesa a ogni giro,
 * annunciando un guasto di rete inesistente. Al primo TIMEOUT con bucket si
 * riprova senza, e per il resto della sessione si chiede come si e' sempre
 * chiesto.
 */
let serverSenzaBucket = false;

export function getOnlineUsers(bucket?: RatingBucket): Promise<RispostaListaOnline> {
  const chiediSenzaBucket = () => richiediListaOnline(undefined);
  if (!bucket || serverSenzaBucket) return chiediSenzaBucket();

  return richiediListaOnline(bucket).catch((err: unknown) => {
    if (!(err instanceof ErroreListaOnline) || err.code !== 'TIMEOUT') throw err;
    serverSenzaBucket = true;
    log.warn('Socket', 'online:list: nessun ack col bucket, si riprova senza');
    return chiediSenzaBucket();
  });
}

function richiediListaOnline(bucket?: RatingBucket): Promise<RispostaListaOnline> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new ErroreListaOnline('NOT_CONNECTED', 'Not connected to server'));
      return;
    }

    let concluso = false;
    const timer = setTimeout(() => {
      if (concluso) return;
      concluso = true;
      log.warn('Socket', 'online:list: nessun ack entro il tetto', { entroMs: ATTESA_ACK_LISTA_ONLINE_MS });
      reject(new ErroreListaOnline('TIMEOUT', 'online:list ack timed out'));
    }, ATTESA_ACK_LISTA_ONLINE_MS);

    log.info('Socket', 'emit online:list', { bucket: bucket ? `${bucket.variant}:${bucket.cadence}` : 'nessuno' });
    // The ack has always carried `counts`; the old narrower type silently threw
    // it away, so the split counters could only ever arrive by broadcast.
    const ack = (response: RispostaListaOnline) => {
      if (concluso) return;
      concluso = true;
      clearTimeout(timer);
      resolve(response);
    };
    // Senza bucket si emette con la firma di sempre: un server vecchio vuole il
    // callback come primo argomento e non ne accetta altri.
    if (bucket) socket.emit('online:list', bucket, ack);
    else socket.emit('online:list', ack);
  });
}

/** Solo per i test: torna a fidarsi del bucket dopo un TIMEOUT simulato. */
export function ripristinaBucketListaOnline(): void {
  serverSenzaBucket = false;
}

// ========== Spectating ==========

/**
 * Chi vuole sapere quando il socket torna.
 *
 * I riaggancio che gia' esistono — `rearmSpectating`, `rearmArena`,
 * `ridichiaraPresenzaArena` — sono cablati a mano dentro `handleConnected`, uno
 * per uno. Questo e' il primitivo che mancava, e serve perche' la chiave
 * `onConnect` del registro dei callback e' gia' del multiplayer-store:
 * registrarne un'altra la sovrascriverebbe, ed e' esattamente il difetto
 * documentato in `arena-store` (e riparato nel torneo). Un insieme di
 * ascoltatori non ha chiavi da rubare a nessuno.
 *
 * Il primo caso d'uso: la lista dei collegati, che senza questo aspettava fino
 * a trenta secondi il proprio giro di ricarica mentre il socket era gia' tornato
 * da ventotto.
 */
const ascoltatoriConnessione = new Set<() => void>();

/** Si iscrive al ritorno del socket. La funzione restituita disiscrive. */
export function subscribeSocketConnected(ascoltatore: () => void): () => void {
  ascoltatoriConnessione.add(ascoltatore);
  return () => {
    ascoltatoriConnessione.delete(ascoltatore);
  };
}

function avvisaConnessione(): void {
  for (const ascoltatore of ascoltatoriConnessione) {
    try {
      ascoltatore();
    } catch (err) {
      /* Un iscritto che esplode non deve impedire agli altri di rientrare. */
      log.error('Socket', 'ascoltatore di connessione fallito', err);
    }
  }
}

/**
 * The player currently being watched, remembered here rather than in the store.
 *
 * Socket.IO rooms belong to a single connection: after a reconnect (or a socket
 * recreation, or a same-user eviction from a second tab) the `spec:` membership
 * is gone and the screen would freeze in silence. Re-arming lives in this
 * module, next to the `connect` handler, because the callback registry is
 * merged by key — a store registering its own `onConnect` would silently
 * replace the multiplayer store's, which is the only thing that re-checks for
 * an active game on reconnect.
 */
let spectatingUserId: string | null = null;
/**
 * The room we were watching. The re-arm resolves a USER to whatever game they
 * are in now, so without this a viewer whose watched game ended would be
 * silently dropped into that player's *next* game after a reconnect.
 */
let spectatingRoomId: string | null = null;

export type SpectateAck = { snapshot?: SpectateSnapshot } & Partial<SpectateError>;

/**
 * Re-join the watched game after the socket came back. Called from the
 * `connect` handler, so it also covers the cases a component-level effect
 * would miss: a socket recreated by `initSocket`, or this tab being evicted
 * and reconnected because the user opened a second one.
 */
function rearmSpectating(): void {
  const targetUserId = spectatingUserId;
  const expectedRoomId = spectatingRoomId;
  if (!targetUserId || !socket) return;
  log.info('Socket', 're-arming spectate after reconnect', { targetUserId, expectedRoomId });
  const fallita = (motivo: string): void => {
    spectatingUserId = null;
    spectatingRoomId = null;
    callbacks.onSpectateUnavailable?.({ error: motivo, code: 'UNAVAILABLE' });
  };
  socket.emit('spectate:start', { targetUserId }, conTetto<SpectateAck>(
    'spectate:start (re-arm)',
    (response) => {
      // Same game, or nothing: following someone into a game the viewer never
      // chose to watch would be a surprise, not a convenience.
      if (response?.snapshot && (!expectedRoomId || response.snapshot.roomId === expectedRoomId)) {
        callbacks.onSpectateSnapshot?.(response.snapshot);
        return;
      }
      if (response?.snapshot) {
        log.info('Socket', 're-arm landed on a different game — dropping', {
          expectedRoomId,
          got: response.snapshot.roomId,
        });
        socket?.emit('spectate:stop');
      }
      spectatingUserId = null;
      spectatingRoomId = null;
      callbacks.onSpectateUnavailable?.({
        error: response?.error ?? 'That game is no longer available',
        code: response?.code ?? 'UNAVAILABLE',
      });
    },
    // Senza tetto un ack perduto lasciava lo spettatore su una damiera ferma,
    // senza messaggio e senza mai completare il ri-aggancio.
    () => fallita('That game is no longer available'),
  ));
}

/**
 * Chi si sta guardando, come generazione.
 *
 * Serve perche' l'ack di `spectate:start` puo' tornare DOPO che si e' smesso:
 * si apre lo spettatore e nello stesso istante l'arena accoppia, il client
 * porta sulla propria partita e smonta lo spettatore. Senza questo contatore
 * l'ack in ritardo riscriveva `spectatingUserId`/`spectatingRoomId`, e da li'
 * il primo `rearmSpectating()` utile rimetteva in ascolto la partita di un
 * estraneo — che poi si riversa sulla damiera condivisa a ogni sua mossa.
 *
 * Lo store ha gia' il suo `session` per la stessa ragione
 * (`spectator-store.ts:84-90`); questo protegge lo stato di modulo, che quello
 * non vede.
 */
let spectateGenerazione = 0;

/** Start watching a player's live game. Resolves with the full snapshot or an error code. */
export function startSpectating(targetUserId: string): Promise<SpectateAck> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      resolve({ error: 'Not connected to server', code: 'UNAVAILABLE' });
      return;
    }
    const generazione = ++spectateGenerazione;
    log.info('Socket', 'emit spectate:start', { targetUserId });
    socket.emit('spectate:start', { targetUserId }, conTetto<SpectateAck>(
      'spectate:start',
      (response) => {
        if (generazione !== spectateGenerazione) {
          /* Si e' gia' smesso (o si guarda qualcun altro): lo `spectate:stop`
             partito dopo il nostro `start` ha gia' fatto uscire il server dalla
             stanza, quindi qui basta non riaccendere lo stato di modulo. */
          log.info('Socket', 'ack spectate:start scaduto — ignorato', { targetUserId });
          resolve(response);
          return;
        }
        if (response?.snapshot) {
          spectatingUserId = targetUserId;
          spectatingRoomId = response.snapshot.roomId;
        }
        resolve(response);
      },
      () => resolve({ error: 'No server acknowledgement', code: 'UNAVAILABLE' }),
    ));
  });
}

/** Ask for a fresh snapshot — the client does this when a tick reports a ply it doesn't have. */
export function resyncSpectating(roomId: string): Promise<SpectateAck> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      resolve({ error: 'Not connected to server', code: 'UNAVAILABLE' });
      return;
    }
    socket.emit('spectate:resync', { roomId }, conTetto<SpectateAck>(
      'spectate:resync',
      resolve,
      () => resolve({ error: 'No server acknowledgement', code: 'UNAVAILABLE' }),
    ));
  });
}

export function stopSpectating(): void {
  spectateGenerazione++;
  spectatingUserId = null;
  spectatingRoomId = null;
  if (!socket?.connected) return;
  log.info('Socket', 'emit spectate:stop');
  socket.emit('spectate:stop');
}

/** Who is being watched, if anyone — used to re-arm after a reconnect. */
export function getSpectatingUserId(): string | null {
  return spectatingUserId;
}

// ========== Challenges ==========

/**
 * Send a challenge to another player.
 *
 * Settings (variant, time control, color, rated) travel as one object so new
 * game modes can be added without threading extra positional args.
 */
export function sendChallenge(
  toUserId: string,
  settings: ChallengeSettings
): Promise<{ challenge: Challenge }> {
  const { variant, timeControl, color, matchType, rated, gameMode, opening, legs, position } = settings;
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:send', { toUserId, variant, timeControl, color, matchType, gameMode, opening, legs, hasPosition: !!position });
    socket.emit(
      'challenge:send',
      { toUserId, variant, timeControl, color, matchType, rated, gameMode, opening, legs, position },
      conTetto<{ challenge?: Challenge; error?: string; code?: string }>(
        'challenge:send',
        (response) => {
          if (response.error) {
            log.warn('Socket', 'challenge:send ack error', { error: response.error, code: response.code });
            /*
             * Col CODICE, non con la sola frase.
             *
             * `response.error` e' inglese e scritto per i log («Player is
             * currently in a game»), e la regola del client e' che a schermo va
             * una traduzione scelta sul codice (vedi `chiave-errore-online.ts`).
             * Finche' qui si lanciava un `Error` nudo, `codiceErrore` non trovava
             * nessun `.code` e ogni rifiuto — «sei gia' in gara», «quel giocatore
             * puo' giocare solo amichevoli» — sarebbe diventato la frase generica.
             *
             * `ArenaSocketError` porta il codice ed e' quello che `codiceErrore`
             * riconosce PER FORMA. Il nome dice «arena» perche' e' nato li', ma
             * quello che fa e' «il rifiuto di un gestore socket, col suo codice»:
             * e' il motivo per cui `codice-errore.ts` lo descrive gia' in quei
             * termini. Una seconda classe identica sarebbe stata peggio del nome.
             */
            reject(new ArenaSocketError(response.code ?? 'UNKNOWN', response.error));
          } else if (response.challenge) {
            log.info('Socket', 'challenge:send ack ok', { challengeId: response.challenge.id });
            resolve({ challenge: response.challenge });
          } else {
            log.warn('Socket', 'challenge:send ack invalid response');
            reject(new Error('Invalid response'));
          }
        },
        () => reject(new AckTimeoutError('challenge:send')),
      )
    );
  });
}

/**
 * Accept an incoming challenge
 */
export function acceptChallenge(challengeId: string): Promise<{ roomId: string; color: 'white' | 'black' }> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:accept', { challengeId });
    socket.emit(
      'challenge:accept',
      challengeId,
      conTetto<{ roomId?: string; color?: 'white' | 'black'; error?: string }>(
        'challenge:accept',
        (response) => {
          if (response.error) {
            log.warn('Socket', 'challenge:accept ack error', { challengeId, error: response.error });
            reject(new Error(response.error));
          } else if (response.roomId && response.color) {
            log.info('Socket', 'challenge:accept ack ok', { roomId: response.roomId, color: response.color });
            resolve({ roomId: response.roomId, color: response.color });
          } else {
            log.warn('Socket', 'challenge:accept ack invalid response', { challengeId });
            reject(new Error('Invalid response'));
          }
        },
        () => reject(new AckTimeoutError('challenge:accept')),
      )
    );
  });
}

/**
 * Decline an incoming challenge
 */
export function declineChallenge(challengeId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:decline', { challengeId });
    socket.emit(
      'challenge:decline',
      challengeId,
      conTetto<{ success?: boolean; error?: string }>(
        'challenge:decline',
        (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        },
        () => reject(new AckTimeoutError('challenge:decline')),
      )
    );
  });
}

/**
 * Cancel an outgoing challenge
 */
export function cancelChallenge(challengeId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:cancel', { challengeId });
    socket.emit(
      'challenge:cancel',
      challengeId,
      conTetto<{ success?: boolean; error?: string }>(
        'challenge:cancel',
        (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        },
        () => reject(new AckTimeoutError('challenge:cancel')),
      )
    );
  });
}

// ========== Challenge links ("invita un amico") ==========

/**
 * Create a shareable challenge link with the settings picked in the challenge
 * modal. No opponent: whoever opens the URL first takes the game.
 */
export function createLinkChallenge(settings: ChallengeSettings): Promise<{ link: LinkChallengeInfo }> {
  const { variant, timeControl, color, matchType, rated, gameMode, opening, legs, position } = settings;
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:create-link', { variant, timeControl, color, matchType, gameMode, opening, legs, hasPosition: !!position });
    socket.emit(
      'challenge:create-link',
      { variant, timeControl, color, matchType, rated, gameMode, opening, legs, position },
      conTetto<{ link?: LinkChallengeInfo; error?: string }>(
        'challenge:create-link',
        (response) => {
          if (response.error) {
            log.warn('Socket', 'challenge:create-link ack error', { error: response.error });
            reject(new Error(response.error));
          } else if (response.link) {
            log.info('Socket', 'challenge:create-link ack ok', { linkId: response.link.id });
            resolve({ link: response.link });
          } else {
            reject(new Error('Invalid response'));
          }
        },
        () => reject(new AckTimeoutError('challenge:create-link')),
      )
    );
  });
}

/** Cancel a challenge link (challenger side). */
export function cancelLinkChallenge(linkId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:cancel-link', { linkId });
    socket.emit('challenge:cancel-link', linkId, conTetto<{ success?: boolean; error?: string }>(
      'challenge:cancel-link',
      (response) => {
        if (response.error) reject(new Error(response.error));
        else resolve();
      },
      () => reject(new AckTimeoutError('challenge:cancel-link')),
    ));
  });
}

/**
 * Accept a challenge link. Resolves with the room when the game starts right
 * away, or with `{ waiting: true }` when the challenger is offline — in that
 * case the claim is held and `game:start` arrives on its own once they return.
 */
export function acceptLinkChallenge(
  linkId: string
): Promise<{ roomId?: string; color?: 'white' | 'black'; waiting?: boolean; link?: LinkChallengeInfo }> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:accept-link', { linkId });
    socket.emit(
      'challenge:accept-link',
      linkId,
      conTetto<{ roomId?: string; color?: 'white' | 'black'; waiting?: boolean; link?: LinkChallengeInfo; error?: string }>(
        'challenge:accept-link',
        (response) => {
          if (response.error) {
            log.warn('Socket', 'challenge:accept-link ack error', { linkId, error: response.error });
            reject(new Error(response.error));
          } else {
            log.info('Socket', 'challenge:accept-link ack ok', { roomId: response.roomId, waiting: response.waiting });
            resolve(response);
          }
        },
        () => reject(new AckTimeoutError('challenge:accept-link')),
      )
    );
  });
}

/** Leave the waiting room: the link goes back up for grabs. */
export function releaseLinkChallenge(linkId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit challenge:release-link', { linkId });
    socket.emit('challenge:release-link', linkId, conTetto<{ success?: boolean; error?: string }>(
      'challenge:release-link',
      (response) => {
        if (response.error) reject(new Error(response.error));
        else resolve();
      },
      () => reject(new AckTimeoutError('challenge:release-link')),
    ));
  });
}

// ========== Draw Offer ==========

/**
 * Offer a draw to the opponent
 */
/**
 * L'ack di `game:offer-draw`.
 *
 * `code` e' il MOTIVO (`DRAW_MUST_MOVE`, `DRAW_LIMIT_REACHED`,
 * `DRAW_TOO_FAST`, o i generici `ROOM_NOT_FOUND`/`AUTH_REQUIRED`/…): e'
 * l'unico campo dove `codiceErrore()` lo cerca, e l'unico da cui si possa
 * ricavare una frase tradotta. `error` resta la frase inglese del server, per
 * i log, e non va mai a schermo.
 *
 * `offertePatta` e' il freno visto dal server — quante proposte restano a chi
 * ha chiesto, e se deve muovere prima di riproporre. Arriva anche sui rifiuti:
 * un rifiuto e' proprio il momento in cui il conto del client e quello del
 * server possono essere diversi.
 */
export type RispostaOffertaPatta = {
  success?: boolean;
  error?: string;
  code?: string;
  offertePatta?: { restanti: number; deviMuovere: boolean };
};

export function offerDraw(roomId: string): Promise<RispostaOffertaPatta> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      resolve({ error: 'Not connected to server' });
      return;
    }
    log.info('Socket', 'emit game:offer-draw', { roomId });
    socket.emit('game:offer-draw', roomId, conTetto<RispostaOffertaPatta>(
      'game:offer-draw',
      (response) => {
        if (response.error) {
          log.error('Socket', 'Draw offer error', response.error);
        }
        resolve(response);
      },
      () => resolve({ error: 'No server acknowledgement' }),
    ));
  });
}

/**
 * La risposta ad Accetta / Rifiuta la patta.
 *
 * `ended` arriva quando la partita e' gia' finita — tipicamente perche' chi
 * aveva proposto ha abbandonato un attimo prima: e' la stessa forma della
 * risposta di `game:resign` (`RispostaAbbandono`), e con quella il client
 * disegna la fine partita invece di lasciare una barra che non risponde.
 */
export type RispostaPatta = RispostaAbbandono;

function rispondiAllaPatta(evento: 'game:accept-draw' | 'game:decline-draw', roomId: string): Promise<RispostaPatta> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      log.warn('Socket', `${evento} non spedito: socket non connesso`, { roomId });
      resolve({ error: 'NOT_CONNECTED' });
      return;
    }
    log.info('Socket', `emit ${evento}`, { roomId });
    socket.emit(evento, roomId, conTetto<RispostaPatta>(
      evento,
      (risposta) => {
        if (risposta?.error) log.warn('Socket', `${evento} rifiutato`, { roomId, error: risposta.error });
        resolve(risposta ?? {});
      },
      () => resolve({ timedOut: true }),
    ));
  });
}

/**
 * Accept an incoming draw offer
 */
export function acceptDraw(roomId: string): Promise<RispostaPatta> {
  return rispondiAllaPatta('game:accept-draw', roomId);
}

/**
 * Decline an incoming draw offer
 */
export function declineDraw(roomId: string): Promise<RispostaPatta> {
  return rispondiAllaPatta('game:decline-draw', roomId);
}

// ========== Takeback (casual online only) ==========

/**
 * Request a takeback, rolling the game back to `toPly` (opponent must consent).
 */
export function requestTakeback(roomId: string, toPly: number): Promise<{ success?: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!socket?.connected) {
      resolve({ error: 'Not connected to server' });
      return;
    }
    log.info('Socket', 'emit game:offer-takeback', { roomId, toPly });
    socket.emit('game:offer-takeback', { roomId, toPly }, conTetto<{ success?: boolean; error?: string }>(
      'game:offer-takeback',
      (response) => {
        if (response.error) {
          log.error('Socket', 'Takeback request error', response.error);
        }
        resolve(response);
      },
      () => resolve({ error: 'No server acknowledgement' }),
    ));
  });
}

/**
 * Accept an incoming takeback request
 */
export function acceptTakeback(roomId: string): void {
  if (!socket?.connected) return;
  log.info('Socket', 'emit game:accept-takeback', { roomId });
  socket.emit('game:accept-takeback', roomId, (response: { success?: boolean; error?: string }) => {
    if (response.error) {
      log.error('Socket', 'Takeback accept error', response.error);
    }
  });
}

/**
 * Decline an incoming takeback request
 */
export function declineTakeback(roomId: string): void {
  if (!socket?.connected) return;
  log.info('Socket', 'emit game:decline-takeback', { roomId });
  socket.emit('game:decline-takeback', roomId, (response: { success?: boolean; error?: string }) => {
    if (response.error) {
      log.error('Socket', 'Takeback decline error', response.error);
    }
  });
}

// ========== Rematch ==========

/**
 * Request a rematch in the current room
 */
export function requestRematch(
  roomId: string
): Promise<{ accepted: boolean; expiresAt?: number }> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit rematch:request', { roomId });
    socket.emit(
      'rematch:request',
      roomId,
      conTetto<{ success?: boolean; accepted?: boolean; expiresAt?: number; error?: string }>(
        'rematch:request',
        (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            // `expiresAt` is absent when the request ACCEPTED a pending offer (the
            // game starts, there is nothing left to count down).
            resolve({ accepted: response.accepted ?? false, expiresAt: response.expiresAt });
          }
        },
        () => reject(new AckTimeoutError('rematch:request')),
      )
    );
  });
}

/**
 * Tell the server this player is ready for the return leg. When both are, the
 * countdown is skipped and the leg starts immediately.
 */
export function setMatchReady(roomId: string): void {
  if (!socket?.connected) {
    log.warn('Socket', 'setMatchReady: not connected', { roomId });
    return;
  }
  log.info('Socket', 'emit match:ready', { roomId });
  socket.emit('match:ready', roomId, () => {});
}

/**
 * Decline a rematch request
 */
export function declineRematch(roomId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit rematch:decline', { roomId });
    socket.emit(
      'rematch:decline',
      roomId,
      conTetto<{ success?: boolean; error?: string }>(
        'rematch:decline',
        (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        },
        () => reject(new AckTimeoutError('rematch:decline')),
      )
    );
  });
}

/**
 * Accept a rematch INVITE received after leaving the game room. The server
 * re-binds this socket to the persisted seat and starts the rematch, which
 * arrives as a normal `rematch:start` event.
 */
export function acceptRematchInvite(roomId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit rematch:accept-invite', { roomId });
    socket.emit(
      'rematch:accept-invite',
      roomId,
      conTetto<{ success?: boolean; error?: string }>(
        'rematch:accept-invite',
        (response) => {
          if (response.error) {
            log.warn('Socket', 'rematch:accept-invite ack error', { roomId, error: response.error });
            reject(new Error(response.error));
          } else {
            resolve();
          }
        },
        () => reject(new AckTimeoutError('rematch:accept-invite')),
      )
    );
  });
}

/**
 * Decline a rematch INVITE received after leaving the game room.
 */
export function declineRematchInvite(roomId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Not connected to server'));
      return;
    }

    log.info('Socket', 'emit rematch:decline-invite', { roomId });
    socket.emit(
      'rematch:decline-invite',
      roomId,
      conTetto<{ success?: boolean; error?: string }>(
        'rematch:decline-invite',
        (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        },
        () => reject(new AckTimeoutError('rematch:decline-invite')),
      )
    );
  });
}
